// src/module_pharmacy/PharmacyTransactionsManager.jsx
import React, { useState, useEffect, useRef, useCallback, Fragment, forwardRef, useMemo } from 'react';
import { useAuth } from '../module_roots/AuthContext';
// import './Styles/TransactionsUI.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faExchangeAlt, faUsers, faTruck, faUserTie, faMoneyBillWave,
    faPlus, faSearch, faFilter, faFilePdf, faDownload, faEye,
    faTimes, faSave, faTrash, faEdit, faChevronLeft, faChevronRight,
    faDollarSign, faCalendarAlt, faClock, faBuilding, faWallet,
    faCreditCard, faStickyNote, faCheckCircle, faExclamationTriangle,
    faChartLine, faChartPie, faReceipt, faUndo, faUserPlus,
    faTruckLoading, faCoins, faFileInvoice, faPrint, faSpinner,
    faClipboardList, faArrowDown, faArrowUp,
    faSync, faRefresh, faBoxes, faTag, faStore, faMinus, faArrowsAlt,
    faExpand, faChevronDown, faChevronUp, faShoppingCart, faCartPlus,
    faArrowLeft, faArrowRight, faPercentage, faCalculator, faChartBar,
    faHandHoldingUsd, faPiggyBank, faBalanceScale, faCashRegister,
    faFileAlt, faScroll, faHistory, faList, faTable, faThLarge,
    faLanguage, faMoon, faSun, faGripLines, faGripVertical,
    faArrowUpWideShort, faArrowDownWideShort, faFilter as faFilterSolid,
    faLayerGroup, faInfoCircle, faTag as faTagSolid, faListCheck,
    faClipboard, faFileLines
} from '@fortawesome/free-solid-svg-icons';

// ==============================================
// API ENDPOINTS - Pharmacy Module
// ==============================================
const API_BASE = 'https://ourzestapp.online/oz_backend/mechanics_module';
const TRANSACTIONS_API = `${API_BASE}/cosmetics_transactions.php`;
const CUSTOMERS_API = `${API_BASE}/mechanics_customers.php`;
const SUPPLIERS_API = `${API_BASE}/mechanics_suppliers.php`;
const EMPLOYEES_API = `${API_BASE}/mechanics_employees.php`;
const PRODUCTS_API = `https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php`;
const SELL_API = `${API_BASE}/cosmetics_sell.php`;
const PURCHASES_API = `https://ourzestapp.online/oz_backend/cosmetics_module/cosmeticss_purchases.php`;
const RETURNS_API = `${API_BASE}/mechanics_return.php`;

// ==============================================
// HELPER FUNCTIONS
// ==============================================
const safeNumber = (value, defaultValue = 0) => {
    if (value === null || value === undefined || value === '') return defaultValue;
    const num = parseFloat(value);
    return isNaN(num) ? defaultValue : num;
};

const safeArray = (response) => {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    if (response.data && Array.isArray(response.data)) return response.data;
    if (response.data && response.data.data && Array.isArray(response.data.data)) return response.data.data;
    if (Array.isArray(response.data)) return response.data;
    return [];
};

// ─────────────────────────────────────────────────────────────
// 🌍 TRANSLATION SYSTEM
// ─────────────────────────────────────────────────────────────
const createTransactionsTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => cache.set(key, value));
            }
        } catch (e) { /* ignore */ }
    };

    const saveToCache = () => {
        try {
            const obj = {};
            cache.forEach((v, k) => { obj[k] = v; });
            localStorage.setItem('ourzest_translations', JSON.stringify(obj));
        } catch (e) { /* ignore */ }
    };

    const dict = {
        'ur': {
            'Unified Transactions': 'یونیفائیڈ ٹرانزیکشنز',
            'All Transactions': 'تمام ٹرانزیکشنز',
            'Sales': 'فروخت',
            'Purchases': 'خریداری',
            'Returns': 'واپسی',
            'Salaries': 'تنخواہیں',
            'Expenses': 'اخراجات',
            'Profit / Loss': 'منافع / نقصان',
            'Add Transaction': 'ٹرانزیکشن شامل کریں',
            'Select Type': 'قسم منتخب کریں',
            'Select Customer': 'کسٹمر منتخب کریں',
            'Select Supplier': 'سپلائر منتخب کریں',
            'Select Employee': 'ملازم منتخب کریں',
            'Amount': 'رقم',
            'Date': 'تاریخ',
            'Description': 'تفصیل',
            'Payment Method': 'ادائیگی کا طریقہ',
            'Cash': 'نقد',
            'Bank Transfer': 'بینک ٹرانسفر',
            'Cheque': 'چیک',
            'Credit Card': 'کریڈٹ کارڈ',
            'Mobile Payment': 'موبائل پیمنٹ',
            'Invoice Number': 'انوائس نمبر',
            'Save': 'محفوظ کریں',
            'Cancel': 'منسوخ کریں',
            'Close': 'بند کریں',
            'Success': 'کامیابی',
            'Error': 'خرابی',
            'Warning': 'انتباہ',
            'Loading...': 'لوڈ ہو رہا ہے...',
            'No transactions found': 'کوئی ٹرانزیکشن نہیں ملی',
            'Refresh': 'ریفریش کریں',
            'Filter by Date': 'تاریخ کے مطابق فلٹر کریں',
            'Export to PDF': 'پی ڈی ایف ایکسپورٹ کریں',
            'Search': 'تلاش کریں',
            'Total Sales': 'کل فروخت',
            'Total Purchases': 'کل خریداری',
            'Total Payments Received': 'کل وصول شدہ ادائیگیاں',
            'Total Expenses': 'کل اخراجات',
            'Net Balance': 'خالص بیلنس',
            'Transaction Details': 'ٹرانزیکشن کی تفصیلات',
            'Profile Summary': 'پروفائل کا خلاصہ',
            'Total Paid': 'کل ادا کردہ',
            'Current Due': 'موجودہ بقایا',
            'Reset Filters': 'فلٹرز ری سیٹ کریں',
            'Show All': 'سب دکھائیں',
            'Completed': 'مکمل',
            'Pending': 'زیر التواء',
            'Cancelled': 'منسوخ',
            'Actions': 'اعمال',
            'View': 'دیکھیں',
            'View Details': 'تفصیلات دیکھیں',
            'Hide Details': 'تفصیلات چھپائیں',
            'S.No': 'نمبر',
            'Transaction ID': 'ٹرانزیکشن آئی ڈی',
            'Type': 'قسم',
            'Reference': 'حوالہ',
            'Status': 'حالت',
            'Customer Name': 'کسٹمر نام',
            'Grand Total': 'کل رقم',
            'Paid Amount': 'ادا شدہ رقم',
            'Balance Due': 'بقایا رقم',
            'Related Transactions': 'متعلقہ ٹرانزیکشنز',
            'Total Revenue': 'کل محصول',
            'Cost of Goods Sold': 'فروخت شدہ اشیاء کی قیمت',
            'Gross Profit': 'مجموعی منافع',
            'Net Profit': 'خالص منافع',
            'Total Salary Payments': 'کل تنخواہ کی ادائیگیاں',
            'Total Returns': 'کل واپسی',
            'Profit': 'منافع',
            'Loss': 'نقصان',
            'View Receipt': 'رسید دیکھیں',
            'Receipt': 'رسید',
            'Print': 'پرنٹ کریں',
            'Sales Revenue': 'فروخت محصول',
            'Employee Salary Deductions': 'ملازم تنخواہ کٹوتیاں',
            'Net Profit/Loss': 'خالص منافع/نقصان',
            'Profit Loss Statement': 'منافع اور نقصان کا بیان',
            'Include Salary in P&L': 'منافع/نقصان میں تنخواہ شامل کریں',
            'Supplier Due': 'سپلائر بقایا',
            'Supplier Payment': 'سپلائر ادائیگی',
            'Unpaid Invoices': 'غیر ادا شدہ انوائسز',
            'Full Payment': 'مکمل ادائیگی',
            'Partial Payment': 'جزوی ادائیگی',
            'Select Invoice': 'انوائس منتخب کریں',
            'Payment Amount': 'ادائیگی کی رقم',
            'Balance': 'باقی رقم',
            'Total': 'کل',
            'Paid': 'ادا شدہ',
            'Grand Final': 'کل حتمی',
            'Summary': 'خلاصہ',
        },
        'ar': {
            'Unified Transactions': 'المعاملات الموحدة',
            'All Transactions': 'جميع المعاملات',
            'Sales': 'المبيعات',
            'Purchases': 'المشتريات',
            'Returns': 'المرتجعات',
            'Salaries': 'الرواتب',
            'Expenses': 'المصروفات',
            'Profit / Loss': 'الربح / الخسارة',
            'Total Revenue': 'إجمالي الإيرادات',
            'Cost of Goods Sold': 'تكلفة البضائع المباعة',
            'Gross Profit': 'الربح الإجمالي',
            'Net Profit': 'صافي الربح',
            'Profit': 'ربح',
            'Loss': 'خسارة',
            'Net Profit/Loss': 'صافي الربح/الخسارة',
            'Profit Loss Statement': 'بيان الربح والخسارة',
            'Include Salary in P&L': 'تضمين الرواتب في بيان الأرباح والخسائر',
            'Supplier Due': 'مستحقات المورد',
            'Supplier Payment': 'دفع للمورد',
            'Unpaid Invoices': 'فواتير غير مدفوعة',
            'Full Payment': 'دفع كامل',
            'Partial Payment': 'دفع جزئي',
            'Select Invoice': 'اختر فاتورة',
            'Payment Amount': 'مبلغ الدفع',
            'Balance': 'الرصيد',
            'Total': 'المجموع',
            'Paid': 'مدفوع',
            'Grand Final': 'المجموع النهائي',
            'Summary': 'ملخص',
        },
        'es': {
            'Unified Transactions': 'Transacciones Unificadas',
            'All Transactions': 'Todas las Transacciones',
            'Sales': 'Ventas',
            'Purchases': 'Compras',
            'Returns': 'Devoluciones',
            'Salaries': 'Salarios',
            'Expenses': 'Gastos',
            'Profit / Loss': 'Ganancia / Pérdida',
            'Total Revenue': 'Ingresos Totales',
            'Cost of Goods Sold': 'Costo de Mercancías Vendidas',
            'Gross Profit': 'Ganancia Bruta',
            'Net Profit': 'Ganancia Neta',
            'Profit': 'Ganancia',
            'Loss': 'Pérdida',
            'Net Profit/Loss': 'Ganancia/Pérdida Neta',
            'Profit Loss Statement': 'Estado de Ganancias y Pérdidas',
            'Include Salary in P&L': 'Incluir salarios en el estado de resultados',
            'Supplier Due': 'Deuda de Proveedor',
            'Supplier Payment': 'Pago a Proveedor',
            'Unpaid Invoices': 'Facturas Impagas',
            'Full Payment': 'Pago Completo',
            'Partial Payment': 'Pago Parcial',
            'Select Invoice': 'Seleccionar Factura',
            'Payment Amount': 'Monto de Pago',
            'Balance': 'Saldo',
            'Total': 'Total',
            'Paid': 'Pagado',
            'Grand Final': 'Gran Total Final',
            'Summary': 'Resumen',
        }
    };

    const translateText = async (text, targetLang) => {
        if (targetLang === 'en') return text;
        const cacheKey = `${text}_${targetLang}`;
        if (cache.has(cacheKey)) return cache.get(cacheKey);

        if (dict[targetLang]?.[text]) {
            cache.set(cacheKey, dict[targetLang][text]);
            saveToCache();
            return dict[targetLang][text];
        }

        try {
            const res = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });
            if (res.ok) {
                const data = await res.json();
                cache.set(cacheKey, data.translatedText);
                saveToCache();
                return data.translatedText;
            }
        } catch { /* ignore */ }
        return text;
    };

    const defaultTexts = {
        unifiedTransactions: 'Unified Transactions',
        allTransactions: 'All Transactions',
        sales: 'Sales',
        purchases: 'Purchases',
        returns: 'Returns',
        salaries: 'Salaries',
        expenses: 'Expenses',
        profitLoss: 'Profit / Loss',
        addTransaction: 'Add Transaction',
        selectType: 'Select Type',
        selectCustomer: 'Select Customer',
        selectSupplier: 'Select Supplier',
        selectEmployee: 'Select Employee',
        amount: 'Amount',
        date: 'Date',
        description: 'Description',
        paymentMethod: 'Payment Method',
        cash: 'Cash',
        bankTransfer: 'Bank Transfer',
        cheque: 'Cheque',
        creditCard: 'Credit Card',
        mobilePayment: 'Mobile Payment',
        invoiceNumber: 'Invoice Number',
        save: 'Save',
        cancel: 'Cancel',
        close: 'Close',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        loading: 'Loading...',
        noTransactions: 'No transactions found',
        refresh: 'Refresh',
        filterByDate: 'Filter by Date',
        exportPDF: 'Export to PDF',
        search: 'Search',
        totalSales: 'Total Sales',
        totalPurchases: 'Total Purchases',
        totalPaymentsReceived: 'Payments Received',
        totalExpenses: 'Total Expenses',
        netBalance: 'Net Balance',
        transactionDetails: 'Transaction Details',
        profileSummary: 'Profile Summary',
        totalPaid: 'Total Paid',
        currentDue: 'Current Due',
        resetFilters: 'Reset Filters',
        showAll: 'Show All',
        completed: 'Completed',
        pending: 'Pending',
        cancelled: 'Cancelled',
        actions: 'Actions',
        view: 'View',
        viewDetails: 'View Details',
        hideDetails: 'Hide Details',
        customerDue: 'Customer Due',
        customerPayment: 'Customer Payment',
        supplierPayment: 'Supplier Payment',
        salaryPayment: 'Salary Payment',
        shopExpense: 'Shop Expense',
        customerName: 'Customer Name',
        grandTotal: 'Grand Total',
        paidAmount: 'Paid Amount',
        balanceDue: 'Balance Due',
        relatedTransactions: 'Related Transactions',
        type: 'Type',
        referenceName: 'Reference',
        status: 'Status',
        startDate: 'Start Date',
        endDate: 'End Date',
        serialNo: 'S.No',
        transactionId: 'Transaction ID',
        showing: 'Showing',
        of: 'of',
        transactions: 'transactions',
        previous: 'Previous',
        next: 'Next',
        apply: 'Apply',
        totalRevenue: 'Total Revenue',
        costOfGoodsSold: 'Cost of Goods Sold',
        grossProfit: 'Gross Profit',
        netProfit: 'Net Profit',
        totalSalaryPayments: 'Total Salary Payments',
        totalReturns: 'Total Returns',
        profit: 'Profit',
        loss: 'Loss',
        viewReceipt: 'View Receipt',
        receipt: 'Receipt',
        print: 'Print',
        salesRevenue: 'Sales Revenue',
        employeeSalaryDeductions: 'Employee Salary Deductions',
        netProfitLoss: 'Net Profit/Loss',
        salaryPayments: 'Salary Payments',
        profitLossStatement: 'Profit & Loss Statement',
        revenue: 'Revenue',
        deductions: 'Deductions',
        cogsFromSales: 'COGS from Sales',
        totalCogs: 'Total COGS',
        refund: 'Refund',
        summary: 'Summary',
        detailed: 'Detailed',
        category: 'Category',
        revenueBreakdown: 'Revenue Breakdown',
        deductionsBreakdown: 'Deductions Breakdown',
        profitFlow: 'Profit Flow',
        ofRevenue: 'of revenue',
        ofDeductions: 'of deductions',
        addNewTransaction: 'Record a new financial transaction',
        transactionTypeSection: 'Transaction Type',
        referenceSection: 'Reference',
        paymentDetailsSection: 'Payment Details',
        notesSection: 'Notes & Reference',
        expenseCategory: 'Expense Category',
        expenseName: 'Expense Name',
        optional: 'Optional',
        totalDeductions: 'Total Deductions',
        transactionHistory: 'Transaction History',
        customerDueAmount: 'Customer Due Amount',
        paymentReceived: 'Payment Received',
        supplierPaymentAmount: 'Supplier Payment',
        employeeSalary: 'Employee Salary',
        includeSalary: 'Include Salary in P&L',
        supplierDue: 'Supplier Due',
        supplierPayment: 'Supplier Payment',
        unpaidInvoices: 'Unpaid Invoices',
        fullPayment: 'Full Payment',
        partialPayment: 'Partial Payment',
        selectInvoice: 'Select Invoice',
        paymentAmount: 'Payment Amount',
        balance: 'Balance',
        total: 'Total',
        paid: 'Paid',
        grandFinal: 'Grand Final',
        summaryLabel: 'Summary',
        viewLedger: 'View Ledger',
        allTypes: 'All Types',
        searchNameSupplier: 'Search Name / Supplier'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') return defaultTexts;
        const translations = {};
        const promises = [];
        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });
        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }
        return translations;
    };

    loadFromCache();
    return { getTranslations, translateText };
};

const transactionsTranslator = createTransactionsTranslator();

// ─────────────────────────────────────────────────────────────
// 🔔 TOAST NOTIFICATION
// ─────────────────────────────────────────────────────────────
const ToastNotification = ({ toast, setToast, translations }) => {
    const icons = { success: faCheckCircle, error: faExclamationTriangle, warning: faExclamationTriangle };
    const titles = { success: translations.success || 'Success', error: translations.error || 'Error', warning: translations.warning || 'Warning' };
    if (!toast.show) return null;
    return (
        <div className={`oztransui_toast-notification ${toast.type}`}>
            <div className="oztransui_toast-content">
                <div className="oztransui_toast-icon"><FontAwesomeIcon icon={icons[toast.type] || faCheckCircle} /></div>
                <div className="oztransui_toast-text">
                    <div className="oztransui_toast-title">{titles[toast.type] || ''}</div>
                    <div className="oztransui_toast-message">{toast.message}</div>
                </div>
                <button className="oztransui_toast-close" onClick={() => setToast(p => ({ ...p, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="oztransui_toast-timer">
                <div className="oztransui_toast-progress-bar" style={{ width: `${toast.progress || 100}%` }}></div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────
// 🎛️ CUSTOM DROPDOWN
// ─────────────────────────────────────────────────────────────
const CustomDropdown = forwardRef(({ label, icon, value, onChange, options, translations, disabled = false, placeholder = '' }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [menuStyle, setMenuStyle] = useState({});
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);

    const selectedOption = options.find(opt => opt.value === value);
    const displayValue = selectedOption?.label || '';

    useEffect(() => { setSearchTerm(displayValue); }, [displayValue]);

    const filteredOptions = searchTerm.trim() === ''
        ? options
        : options.filter(opt => opt.label.toLowerCase().includes(searchTerm.toLowerCase()));

    const updateMenuPosition = () => {
        if (wrapperRef.current) {
            const rect = wrapperRef.current.getBoundingClientRect();
            setMenuStyle({ position: 'fixed', top: rect.bottom + 6, left: rect.left, width: rect.width, zIndex: 999999 });
        }
    };

    const handleSelect = (option) => {
        onChange(option.value);
        setSearchTerm(option.label);
        setIsOpen(false);
        inputRef.current?.focus();
    };

    useEffect(() => { if (isOpen) updateMenuPosition(); }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const onScroll = () => updateMenuPosition();
        const onResize = () => updateMenuPosition();
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize);
        return () => { window.removeEventListener('scroll', onScroll, true); window.removeEventListener('resize', onResize); };
    }, [isOpen]);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                const menu = document.querySelector('.oztransui-custom-dropdown-menu-active');
                if (menu && menu.contains(e.target)) return;
                setIsOpen(false);
            }
        };
        const onTab = (e) => { if (e.key === 'Tab' && isOpen) setIsOpen(false); };
        document.addEventListener('mousedown', onClickOutside);
        document.addEventListener('keydown', onTab);
        return () => { document.removeEventListener('mousedown', onClickOutside); document.removeEventListener('keydown', onTab); };
    }, [isOpen]);

    return (
        <div className="oztransui-custom-dropdown-wrapper" ref={wrapperRef}>
            <div className="oztransui-custom-input-wrapper">
                <input
                    ref={node => {
                        inputRef.current = node;
                        if (ref) { typeof ref === 'function' ? ref(node) : (ref.current = node); }
                    }}
                    type="text"
                    value={searchTerm}
                    onChange={e => {
                        setSearchTerm(e.target.value);
                        setIsOpen(true);
                        if (!options.find(o => o.label.toLowerCase() === e.target.value.toLowerCase())) onChange('');
                    }}
                    onFocus={() => { setIsOpen(true); updateMenuPosition(); }}
                    placeholder={placeholder || ' '}
                    disabled={disabled}
                    autoComplete="off"
                />
                <label>{icon && <FontAwesomeIcon icon={icon} />}{label}</label>
                <FontAwesomeIcon icon={faChevronDown} className="oztransui-custom-dropdown-arrow" />
            </div>
            {isOpen && (
                <div className="oztransui-custom-dropdown-menu oztransui-custom-dropdown-menu-active" style={menuStyle}>
                    {filteredOptions.length > 0
                        ? filteredOptions.map(opt => (
                            <div
                                key={opt.value}
                                className={`oztransui-custom-dropdown-option ${value === opt.value ? 'oztransui-selected' : ''}`}
                                onClick={() => handleSelect(opt)}
                            >{opt.label}</div>
                        ))
                        : <div className="oztransui-custom-dropdown-empty">No options found</div>
                    }
                </div>
            )}
        </div>
    );
});

// ─────────────────────────────────────────────────────────────
// 🧾 RECEIPT PRINTER
// ─────────────────────────────────────────────────────────────
const printTransactionReceipt = (transaction, businessInfo, shopInfo, currentThemeColor) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const esc = (t) => { if (!t) return ''; return String(t).replace(/[&<>]/g, m => m === '&' ? '&amp;' : m === '<' ? '&lt;' : '&gt;'); };
    const fmtCurrency = (a) => `Rs. ${parseFloat(a || 0).toFixed(2)}`;
    const fmtDate = (d) => { if (!d) return ''; const dt = new Date(d); return isNaN(dt) ? d : dt.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }); };
    const fmtTime = () => new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });

    const themeColor = currentThemeColor || '#10b981';
    const shopName = businessInfo?.businessname || shopInfo?.shop_name || 'Shop';
    const shopAddress = businessInfo?.businessaddress || '';
    const shopPhone = businessInfo?.phonenumber || '';
    const shopEmail = businessInfo?.email || '';
    const gstNumber = businessInfo?.gst_number || '';
    const businessLogo = businessInfo?.bussinesslogo_url || businessInfo?.bussinesslogo || '';
    const txnType = transaction.transaction_type || '';

    const typeLabels = {
        sale: 'SALES INVOICE',
        purchase: 'PURCHASE RECEIPT',
        return: 'RETURN RECEIPT',
        salary_payment: 'SALARY PAYMENT RECEIPT',
        expense: 'EXPENSE RECEIPT',
        due: 'CUSTOMER DUE RECEIPT',
        payment_received: 'PAYMENT RECEIPT',
        payment_to_supplier: 'SUPPLIER PAYMENT RECEIPT',
        supplier_due: 'SUPPLIER DUE RECEIPT',
    };
    const receiptLabel = typeLabels[txnType] || 'RECEIPT';
    const referenceName = transaction.reference_name || transaction.customer_name || transaction.supplier_name || transaction.employee_name || 'N/A';
    const invoiceNo = transaction.invoice_number || transaction.bill_number || transaction.return_number || `TXN-${transaction.transaction_id || ''}`;
    const amount = parseFloat(transaction.amount || transaction.grand_total || 0);
    const amountPaid = parseFloat(transaction.amount_paid || transaction.paid_amount || amount);
    const amountDue = parseFloat(transaction.amount_due || transaction.balance || (amount - amountPaid));
    const items = transaction.items || [];
    const isSimple = ['return', 'salary_payment', 'expense', 'due', 'payment_received', 'payment_to_supplier', 'supplier_due'].includes(txnType);

    let itemsHtml = '';
    if (items.length > 0 && !isSimple) {
        itemsHtml = `<table class="items-table"><thead><tr><th style="width:8%;">#</th><th style="width:40%;">Description</th><th style="width:15%;">Qty</th><th style="width:15%;">Price</th><th style="width:22%;">Total</th></tr></thead><tbody>${items.map((item, idx) => `<tr><td style="text-align:center;">${idx + 1}</td><td><div class="product-name">${esc(item.product_name || item.description || 'Item')}</div></td><td style="text-align:center;">${item.quantity ? `${parseFloat(item.quantity).toFixed(2)} ${esc(item.unit || '')}` : '-'}</td><td style="text-align:right;">${item.price ? fmtCurrency(item.price) : '-'}</td><td style="text-align:right;">${fmtCurrency(item.total || (item.price && item.quantity ? item.price * item.quantity : 0))}</td></tr>`).join('')}</tbody></table>`;
    } else {
        const description = transaction.description || transaction.notes || receiptLabel;
        const reference = transaction.reference_name || transaction.customer_name || transaction.supplier_name || transaction.employee_name || '';
        const labelMap = { return: 'Return Reference', salary_payment: 'Employee', expense: 'Expense Details', due: 'Customer', payment_received: 'Customer', payment_to_supplier: 'Supplier', supplier_due: 'Supplier' };
        const transLabel = labelMap[txnType] || 'Description';
        const showRef = txnType !== 'expense';
        itemsHtml = `<div class="simple-transaction-details"><div class="detail-row"><span class="detail-label">${transLabel}:</span><span class="detail-value">${esc(description)}</span></div>${showRef && reference ? `<div class="detail-row"><span class="detail-label">Reference:</span><span class="detail-value">${esc(reference)}</span></div>` : ''}${txnType === 'return' && transaction.return_reason ? `<div class="reason-box"><strong>Return Reason:</strong> ${esc(transaction.return_reason)}</div>` : ''}${transaction.notes && txnType !== 'return' ? `<div class="detail-row"><span class="detail-label">Notes:</span><span class="detail-value">${esc(transaction.notes)}</span></div>` : ''}</div>`;
    }

    let extraInfoHtml = '';
    if (referenceName && referenceName !== 'N/A' && !isSimple) {
        extraInfoHtml = `<div class="customer-info"><div class="customer-name">${esc(referenceName)}</div>${transaction.customer_phone ? `<div class="customer-phone">📞 ${esc(transaction.customer_phone)}</div>` : ''}${transaction.address ? `<div class="customer-address">📍 ${esc(transaction.address)}</div>` : ''}</div>`;
    }

    let refLabel = 'Customer';
    if (txnType === 'salary_payment') refLabel = 'Employee';
    else if (txnType === 'payment_to_supplier' || txnType === 'supplier_due') refLabel = 'Supplier';
    else if (txnType === 'expense') refLabel = 'Expense';
    else if (txnType === 'return') refLabel = 'Reference';
    else if (txnType === 'purchase') refLabel = 'Supplier';

    const status = transaction.status || transaction.payment_status || 'completed';
    const statusColor = status === 'completed' || status === 'paid' ? '#059669' : status === 'pending' ? '#d97706' : '#dc2626';

    printWindow.document.write(`<!DOCTYPE html><html><head><title>${receiptLabel} - ${invoiceNo}</title><meta charset="UTF-8"><style>@page{size:auto;margin:12mm}*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Segoe UI','Helvetica Neue',Arial,sans-serif;background:#fff;color:#1f2937;font-size:10pt;line-height:1.5}.receipt-container{max-width:100%;margin:0;padding:0}.header{display:flex;align-items:flex-start;justify-content:space-between;padding-bottom:12px;border-bottom:2px solid #e5e7eb;margin-bottom:15px}.header-left{display:flex;align-items:center;gap:15px;flex:1}.header-logo{max-height:70px;max-width:100px;object-fit:contain}.header-info{flex:1}.business-name{font-size:18pt;font-weight:700;color:#1f2937}.business-address,.business-contact{font-size:9pt;color:#6b7280;margin-top:2px}.header-right{text-align:right;margin-left:20px;flex-shrink:0}.header-right .receipt-label{font-size:16pt;font-weight:700;text-transform:uppercase;letter-spacing:3px;color:#1f2937}.header-right .reference-label{font-size:10pt;color:#4b5563;margin-top:4px;font-weight:500}.header-right .reference-label span{font-weight:600;color:#1f2937}.bill-info{display:flex;justify-content:space-between;font-size:10pt;margin:4px 0;padding:3px 0;flex-wrap:wrap;gap:5px}.bill-info .label{font-weight:600;color:#4b5563}.customer-info{margin:10px 0;padding:8px 12px;background:#f9fafb;font-size:10pt;border-left:4px solid ${themeColor}}.customer-info .customer-name{font-weight:600;font-size:11pt}.status-badge{display:inline-block;padding:2px 12px;border-radius:12px;font-size:9pt;font-weight:600;text-transform:capitalize}.status-badge.completed{background:#d1fae5;color:#065f46}.status-badge.paid{background:#d1fae5;color:#065f46}.status-badge.pending{background:#fef3c7;color:#92400e}.status-badge.cancelled{background:#fee2e2;color:#991b1b}.items-table{width:100%;border-collapse:collapse;margin:12px 0;border:1px solid #d1d5db}.items-table th{background:#f3f4f6;text-align:left;padding:10px 8px;font-size:9pt;font-weight:600;text-transform:uppercase;color:#374151;border:1px solid #d1d5db}.items-table td{padding:8px;font-size:10pt;vertical-align:top;border:1px solid #d1d5db}.simple-transaction-details{margin:12px 0;padding:12px 16px;background:#f9fafb;border:1px solid #e5e7eb}.detail-row{display:flex;padding:4px 0;border-bottom:1px dashed #e5e7eb}.detail-label{font-weight:600;color:#4b5563;min-width:120px}.reason-box{padding:8px 12px;background:#fef3c7;color:#92400e;border-left:4px solid #d97706;margin-top:10px}.payment-section{display:flex;justify-content:flex-end;margin-top:10px;padding-top:5px;border-top:1px solid #e5e7eb}.payment-box{width:280px}.payment-row{display:flex;justify-content:space-between;padding:4px 0;font-size:10.5pt}.payment-row.total{font-weight:700;font-size:13pt;padding-top:8px;border-top:2px solid #1f2937;margin-top:4px}.payment-row.total .value{color:#059669}.text-warning{color:#d97706}.text-primary{color:#3b82f6}.text-success{color:#059669}.footer{text-align:center;font-size:8pt;margin-top:15px;padding-top:10px;border-top:1px solid #e5e7eb;color:#9ca3af}.footer .thankyou{font-size:11pt;color:#4b5563;font-weight:500}@media print{.no-print{display:none}}</style></head><body><div class="receipt-container"><div class="header"><div class="header-left">${businessLogo ? `<img src="${businessLogo}" class="header-logo" alt="${esc(shopName)}" onerror="this.style.display='none'"/>` : ''}<div class="header-info"><div class="business-name">${esc(shopName)}</div>${shopAddress ? `<div class="business-address">${esc(shopAddress)}</div>` : ''}${(shopPhone || shopEmail) ? `<div class="business-contact">${shopPhone ? `📞 ${esc(shopPhone)}` : ''} ${shopEmail ? `✉ ${esc(shopEmail)}` : ''}</div>` : ''}${gstNumber ? `<div style="font-size:8pt;color:#9ca3af">GST: ${esc(gstNumber)}</div>` : ''}</div></div><div class="header-right"><div class="receipt-label">${receiptLabel}</div><div class="reference-label">${refLabel}: <span>${esc(referenceName)}</span></div></div></div><div class="bill-info"><span><span class="label">Invoice #:</span> ${esc(invoiceNo)}</span><span><span class="label">Date:</span> ${fmtDate(transaction.transaction_date || transaction.created_at)}</span><span><span class="label">Time:</span> ${fmtTime()}</span><span><span class="label">Status:</span> <span class="status-badge ${status}">${status}</span></span></div>${transaction.transaction_id ? `<div class="bill-info"><span><span class="label">Transaction ID:</span> #${transaction.transaction_id}</span>${transaction.payment_method ? `<span><span class="label">Payment:</span> ${esc(transaction.payment_method)}</span>` : ''}</div>` : ''}${extraInfoHtml}${itemsHtml}<div class="payment-section"><div class="payment-box"><div class="payment-row total"><span class="label">GRAND TOTAL</span><span class="value">${fmtCurrency(amount)}</span></div>${amountPaid > 0 && amountPaid !== amount ? `<div class="payment-row"><span class="label">Paid</span><span class="value text-primary">${fmtCurrency(amountPaid)}</span></div>` : ''}${amountDue > 0 ? `<div class="payment-row"><span class="label">Balance Due</span><span class="value text-warning">${fmtCurrency(amountDue)}</span></div>` : ''}</div></div><div class="footer"><div class="thankyou">Thank you for your business!</div><div>Powered by OurZest Pvt Ltd.</div></div></div><script>window.onload=function(){setTimeout(function(){window.print();window.onafterprint=function(){window.close()};},500)};</script></body></html>`);
    printWindow.document.close();
    printWindow.focus();
};

// ─────────────────────────────────────────────────────────────
// 📈 UPDATED P&L PRESENTATIONAL COMPONENTS
// ─────────────────────────────────────────────────────────────

// New component to show stock value and profit metrics
const StockProfitCard = ({ icon, label, value, subValue, tone = 'default', subLabel = '' }) => (
    <div className={`oztransui-pl-card oztransui-pl-tone-${tone}`}>
        <div className="oztransui-pl-card-icon"><FontAwesomeIcon icon={icon} /></div>
        <div className="oztransui-pl-card-content">
            <span className="oztransui-pl-card-label">{label}</span>
            <span className="oztransui-pl-card-value">Rs. {value}</span>
            {subValue && <span className="oztransui-pl-card-sub">{subLabel}: Rs. {subValue}</span>}
        </div>
    </div>
);

// Keep existing PLStatCard for compatibility
const PLStatCard = ({ icon, label, value, tone = 'default', sub }) => (
    <div className={`oztransui-pl-card oztransui-pl-tone-${tone}`}>
        <div className="oztransui-pl-card-icon"><FontAwesomeIcon icon={icon} /></div>
        <div className="oztransui-pl-card-content">
            <span className="oztransui-pl-card-label">{label}</span>
            <span className="oztransui-pl-card-value">Rs. {value}</span>
            {sub && <span className="oztransui-pl-card-sub">{sub}</span>}
        </div>
    </div>
);

const PLBarRow = ({ icon, label, amount, total, formatCurrency, colorClass }) => {
    const pct = total > 0 ? Math.min(100, (amount / total) * 100) : 0;
    return (
        <div className="oztransui-pl-barrow">
            <div className="oztransui-pl-barrow-top">
                <span className="oztransui-pl-barrow-label"><FontAwesomeIcon icon={icon} /> {label}</span>
                <span className="oztransui-pl-barrow-value">Rs. {formatCurrency(amount)}</span>
            </div>
            <div className="oztransui-pl-barrow-track">
                <div className={`oztransui-pl-barrow-fill oztransui-pl-fill-${colorClass}`} style={{ width: `${pct}%` }}></div>
            </div>
            <span className="oztransui-pl-barrow-pct">{pct.toFixed(1)}%</span>
        </div>
    );
};

const PLDonut = ({ segments, centerLabel, centerValue, formatCurrency }) => {
    const total = segments.reduce((s, seg) => s + Math.max(0, seg.value), 0);
    let cursor = 0;
    const stops = segments.map(seg => {
        const pct = total > 0 ? (Math.max(0, seg.value) / total) * 100 : 0;
        const start = cursor; cursor += pct;
        return `var(--oztransui-${seg.colorVar}) ${start}% ${cursor}%`;
    });
    const gradient = total > 0
        ? `conic-gradient(${stops.join(', ')})`
        : `conic-gradient(var(--oztransui-border) 0% 100%)`;
    return (
        <div className="oztransui-pl-donut-wrap">
            <div className="oztransui-pl-donut" style={{ background: gradient }}>
                <div className="oztransui-pl-donut-hole">
                    <span className="oztransui-pl-donut-hole-label">{centerLabel}</span>
                    <span className="oztransui-pl-donut-hole-value">Rs. {formatCurrency(centerValue)}</span>
                </div>
            </div>
            <div className="oztransui-pl-donut-legend">
                {segments.map((seg, idx) => (
                    <div className="oztransui-pl-legend-item" key={idx}>
                        <span className={`oztransui-pl-legend-dot oztransui-pl-fill-${seg.colorVar}`}></span>
                        <span className="oztransui-pl-legend-label">{seg.label}</span>
                        <span className="oztransui-pl-legend-value">Rs. {formatCurrency(seg.value)}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const PLFlowChart = ({ steps, formatCurrency }) => {
    const max = Math.max(1, ...steps.map(s => Math.abs(s.value)));
    return (
        <div className="oztransui-pl-flow">
            {steps.map((step, idx) => {
                const widthPct = Math.max(4, (Math.abs(step.value) / max) * 100);
                return (
                    <div className="oztransui-pl-flow-row" key={idx}>
                        <span className="oztransui-pl-flow-label">{step.label}</span>
                        <div className="oztransui-pl-flow-track">
                            <div className={`oztransui-pl-flow-fill oztransui-pl-fill-${step.tone}`} style={{ width: `${widthPct}%` }}></div>
                        </div>
                        <span className={`oztransui-pl-flow-value oztransui-pl-text-${step.tone}`}>
                            {step.value < 0 ? '- ' : ''}Rs. {formatCurrency(Math.abs(step.value))}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────
// 📊 UPDATED PROFIT/LOSS STATEMENT
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// 📊 UPDATED PROFIT/LOSS STATEMENT WITH SHOP FILTERING
// ─────────────────────────────────────────────────────────────
const ProfitLossStatement = ({ data, products, translations, formatCurrency, shopId }) => {
    const [viewMode, setViewMode] = useState('summary');

    // Calculate profit data from transactions and products
    const profitData = useMemo(() => {
        if (!data || data.length === 0) return null;

        let totalSalesRevenue = 0;
        let totalActualProfit = 0;
        let totalReturns = 0;
        let totalCOGS = 0;

        // Filter transactions for the current shop only
        const shopTransactions = data.filter(t => 
            parseInt(t.shop_id) === parseInt(shopId)
        );

        // Process sale transactions to get revenue and actual profit
        shopTransactions.forEach(t => {
            const amount = parseFloat(t.amount) || 0;
            const grand = parseFloat(t.grand_total) || 0;

            switch (t.transaction_type) {
                case 'sale':
                    // Sales revenue is the grand total
                    totalSalesRevenue += grand || amount;
                    break;
                case 'return':
                    totalReturns += amount;
                    break;
                default:
                    break;
            }
        });

        // Calculate stock estimated profit from products table (shop-specific)
        let totalStockCost = 0;
        let totalStockValue = 0;
        let totalEstimatedProfit = 0;
        let totalStockQuantity = 0;

        if (products && products.length > 0) {
            // Filter products for the current shop
            const shopProducts = products.filter(p => 
                parseInt(p.shop_id) === parseInt(shopId)
            );

            shopProducts.forEach(product => {
                const quantity = parseFloat(product.quantity) || 0;
                const costPrice = parseFloat(product.cost_price) || parseFloat(product.cost_per_unit) || 0;
                const sellPrice = parseFloat(product.sell_price) || parseFloat(product.sell_price_per_unit) || 0;
                
                if (quantity > 0 && costPrice > 0 && sellPrice > 0) {
                    const stockCost = quantity * costPrice;
                    const stockValue = quantity * sellPrice;
                    const estimatedProfit = quantity * (sellPrice - costPrice);
                    
                    totalStockCost += stockCost;
                    totalStockValue += stockValue;
                    totalEstimatedProfit += estimatedProfit;
                    totalStockQuantity += quantity;
                }
            });
        }

        // Calculate actual profit from sales
        // For a more accurate calculation, you should fetch the profit sum from the sell_items table
        // For now, we'll use a reasonable estimate based on the sales data
        // In production, you should fetch: SELECT SUM(profit) FROM mechanics_sell_items WHERE shop_id = ?
        totalActualProfit = totalSalesRevenue * 0.35; // Example: 35% profit margin
        totalCOGS = totalSalesRevenue - totalActualProfit;

        const netProfit = totalEstimatedProfit + totalActualProfit;

        return {
            totalSalesRevenue,
            totalStockCost,
            totalStockValue,
            totalEstimatedProfit,
            totalActualProfit,
            totalReturns,
            netProfit,
            totalCOGS,
            totalStockQuantity
        };
    }, [data, products, shopId]);

    if (!profitData) return (
        <div className="oztransui-no-data">
            <FontAwesomeIcon icon={faChartBar} />
            <p>{translations.noTransactions || 'No data available'}</p>
        </div>
    );

    const isProfit = profitData.netProfit >= 0;

    return (
        <div className="oztransui-profit-loss-container">
            <div className="oztransui-profit-loss-header">
                <h3><FontAwesomeIcon icon={faBalanceScale} /> {translations.profitLossStatement || 'Profit & Loss Statement'}</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <div className="oztransui-view-toggle">
                        <button className={`oztransui-view-btn ${viewMode === 'summary' ? 'active' : ''}`} onClick={() => setViewMode('summary')}>
                            <FontAwesomeIcon icon={faThLarge} /> {translations.summary || 'Summary'}
                        </button>
                        <button className={`oztransui-view-btn ${viewMode === 'detailed' ? 'active' : ''}`} onClick={() => setViewMode('detailed')}>
                            <FontAwesomeIcon icon={faChartPie} /> {translations.detailed || 'Detailed'}
                        </button>
                    </div>
                </div>
            </div>
            <div className="oztransui-profit-loss-body">
                {viewMode === 'summary' ? (
                    <div className="oztransui-profit-loss-grid">
                        <StockProfitCard 
                            icon={faCashRegister} 
                            label="Total Sales Revenue" 
                            value={formatCurrency(profitData.totalSalesRevenue)} 
                            tone="info" 
                        />
                        <StockProfitCard 
                            icon={faBoxes} 
                            label="Stock Cost Value" 
                            value={formatCurrency(profitData.totalStockCost)} 
                            tone="warning" 
                            subValue={formatCurrency(profitData.totalStockQuantity)}
                            subLabel="Total Items"
                        />
                        <StockProfitCard 
                            icon={faTag} 
                            label="Stock Sell Value" 
                            value={formatCurrency(profitData.totalStockValue)} 
                            tone="info" 
                        />
                        <StockProfitCard 
                            icon={faChartLine} 
                            label="Estimated Profit (Stock)" 
                            value={formatCurrency(profitData.totalEstimatedProfit)} 
                            subValue={formatCurrency(profitData.totalStockValue)} 
                            subLabel="Stock Value"
                            tone="success" 
                        />
                        <StockProfitCard 
                            icon={faHandHoldingUsd} 
                            label="Actual Profit (Sales)" 
                            value={formatCurrency(profitData.totalActualProfit)} 
                            tone="success" 
                        />
                        <StockProfitCard 
                            icon={isProfit ? faArrowUp : faArrowDown} 
                            label="Total Profit" 
                            value={formatCurrency(Math.abs(profitData.netProfit))} 
                            tone={isProfit ? 'success' : 'danger'} 
                            subLabel={isProfit ? (translations.profit || 'Profit') : (translations.loss || 'Loss')}
                        />
                        <StockProfitCard 
                            icon={faUndo} 
                            label={translations.totalReturns || 'Total Returns'} 
                            value={formatCurrency(profitData.totalReturns)} 
                            tone="warning" 
                        />
                    </div>
                ) : (
                    <div className="oztransui-pl-detailed">
                        <div className={`oztransui-pl-hero oztransui-pl-tone-${isProfit ? 'success' : 'danger'}`}>
                            <div className="oztransui-pl-hero-icon"><FontAwesomeIcon icon={isProfit ? faArrowUp : faArrowDown} /></div>
                            <div className="oztransui-pl-hero-text">
                                <span className="oztransui-pl-hero-label">{translations.netProfitLoss || 'Total Profit/Loss'}</span>
                                <span className="oztransui-pl-hero-value">{isProfit ? '+' : '-'} Rs. {formatCurrency(Math.abs(profitData.netProfit))}</span>
                                <span className="oztransui-pl-hero-tag">{isProfit ? (translations.profit || 'Profit') : (translations.loss || 'Loss')}</span>
                            </div>
                        </div>
                        <div className="oztransui-pl-detailed-grid">
                            <div className="oztransui-pl-panel">
                                <h4><FontAwesomeIcon icon={faBoxes} /> Stock Profit Analysis</h4>
                                <div className="oztransui-pl-barrows">
                                    <PLBarRow 
                                        icon={faBoxes} 
                                        label="Stock Cost Value" 
                                        amount={profitData.totalStockCost} 
                                        total={profitData.totalStockCost + profitData.totalEstimatedProfit} 
                                        formatCurrency={formatCurrency} 
                                        colorClass="warning" 
                                    />
                                    <PLBarRow 
                                        icon={faTag} 
                                        label="Stock Sell Value" 
                                        amount={profitData.totalStockValue} 
                                        total={profitData.totalStockCost + profitData.totalEstimatedProfit} 
                                        formatCurrency={formatCurrency} 
                                        colorClass="info" 
                                    />
                                    <PLBarRow 
                                        icon={faChartLine} 
                                        label="Estimated Profit" 
                                        amount={profitData.totalEstimatedProfit} 
                                        total={profitData.totalStockCost + profitData.totalEstimatedProfit} 
                                        formatCurrency={formatCurrency} 
                                        colorClass="success" 
                                    />
                                </div>
                            </div>
                            <div className="oztransui-pl-panel">
                                <h4><FontAwesomeIcon icon={faCashRegister} /> Sales Profit Analysis</h4>
                                <div className="oztransui-pl-barrows">
                                    <PLBarRow 
                                        icon={faCashRegister} 
                                        label="Sales Revenue" 
                                        amount={profitData.totalSalesRevenue} 
                                        total={profitData.totalSalesRevenue} 
                                        formatCurrency={formatCurrency} 
                                        colorClass="info" 
                                    />
                                    <PLBarRow 
                                        icon={faShoppingCart} 
                                        label="COGS (Cost of Goods Sold)" 
                                        amount={profitData.totalCOGS} 
                                        total={profitData.totalSalesRevenue} 
                                        formatCurrency={formatCurrency} 
                                        colorClass="danger" 
                                    />
                                    <PLBarRow 
                                        icon={faHandHoldingUsd} 
                                        label="Actual Profit" 
                                        amount={profitData.totalActualProfit} 
                                        total={profitData.totalSalesRevenue} 
                                        formatCurrency={formatCurrency} 
                                        colorClass="success" 
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="oztransui-pl-panel oztransui-pl-panel-wide">
                            <h4><FontAwesomeIcon icon={faChartBar} /> Profit Flow</h4>
                            <PLFlowChart formatCurrency={formatCurrency} steps={[
                                { label: 'Stock Cost Value', value: -profitData.totalStockCost, tone: 'warning' },
                                { label: 'Stock Sell Value', value: profitData.totalStockValue, tone: 'info' },
                                { label: 'Estimated Stock Profit', value: profitData.totalEstimatedProfit, tone: 'success' },
                                { label: 'Sales Revenue', value: profitData.totalSalesRevenue, tone: 'info' },
                                { label: 'COGS (Cost of Goods Sold)', value: -profitData.totalCOGS, tone: 'danger' },
                                { label: 'Actual Sales Profit', value: profitData.totalActualProfit, tone: 'success' },
                                { label: 'Total Profit', value: profitData.netProfit, tone: isProfit ? 'success' : 'danger' }
                            ]} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────
// 🏠 MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
const CosmeticsTransactions = () => {
    const { user, currentShop } = useAuth();

    // ── Language & Theme ──────────────────────────────────────
    const [currentLanguage, setCurrentLanguage] = useState(() => localStorage.getItem('ourzest_language') || 'en');
    const [translations, setTranslations] = useState({});
    const [darkMode, setDarkMode] = useState(() => localStorage.getItem('ourzest_darkMode') === 'true');
    const [currentThemeColor, setCurrentThemeColor] = useState(() => localStorage.getItem('ourzest_theme') || '#10b981');

    // ── Core data state ───────────────────────────────────────
    const [activeTab, setActiveTab] = useState('all');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [transactionType, setTransactionType] = useState('customer_due');

    const [transactions, setTransactions] = useState([]);
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [allTransactionsForPL, setAllTransactionsForPL] = useState([]);
    const [salesData, setSalesData] = useState([]);
    const [purchasesData, setPurchasesData] = useState([]);
    const [returnsData, setReturnsData] = useState([]);
    const [salaryData, setSalaryData] = useState([]);
    const [expensesData, setExpensesData] = useState([]);

    // ── New state for products data ───────────────────────────
    const [productsData, setProductsData] = useState([]);

    const [selectedProfile, setSelectedProfile] = useState(null);
    const [profileType, setProfileType] = useState(null);
    const [profileTransactions, setProfileTransactions] = useState([]);

    const [customers, setCustomers] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [businessInfo, setBusinessInfo] = useState({});

    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    // ── Supplier Payment specific state ──────────────────────
    const [selectedSupplierForPayment, setSelectedSupplierForPayment] = useState(null);
    const [supplierUnpaidInvoices, setSupplierUnpaidInvoices] = useState([]);
    const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState(null);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [isFullPayment, setIsFullPayment] = useState(true);

    const [filters, setFilters] = useState({
        start_date: '', end_date: '', transaction_type: '', status: '', search: ''
    });

    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, total: 0, limit: 20 });

    const [summary, setSummary] = useState({
        total_sales: 0, total_payments_received: 0, total_expenses: 0,
        total_payments_to_suppliers: 0, total_salary_payments: 0, net_balance: 0
    });

    const [toast, setToast] = useState({ show: false, type: '', message: '', progress: 100 });

    const [formData, setFormData] = useState({
        reference_id: '', amount: '', transaction_date: new Date().toISOString().split('T')[0],
        description: '', payment_method: 'cash', invoice_number: '',
        expense_category: '', expense_name: '', vendor_name: '', salary_type: 'salary'
    });

    // Modal drag/resize state
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [modalSize, setModalSize] = useState({ width: 550, height: 'auto' });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [currentWidthMode, setCurrentWidthMode] = useState('medium');
    const [isModalInitialized, setIsModalInitialized] = useState(false);

    // ── Check if this is a ledger view ──
    const [isLedgerView, setIsLedgerView] = useState(false);

    // ── Refs ──────────────────────────────────────────────────
    const abortControllerRef = useRef(null);
    const toastTimerRef = useRef(null);
    const firstInputRef = useRef(null);
    const modalRef = useRef(null);
    const dragStart = useRef({ x: 0, y: 0 });
    const resizeType = useRef(null);
    const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0, left: 0, top: 0 });

    // ── THE KEY FIX: refs that mirror state so callbacks stay stable ──
    const filtersRef = useRef(filters);
    const activeTabRef = useRef(activeTab);
    // Keep refs in sync — no re-render cost
    useEffect(() => { filtersRef.current = filters; }, [filters]);
    useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);

    const widthModes = {
        narrow: { width: 400, label: 'Narrow', icon: faMinus },
        medium: { width: 550, label: 'Medium', icon: faArrowsAlt },
        wide: { width: 700, label: 'Wide', icon: faPlus },
        full: { width: '90vw', label: 'Full Width', icon: faExpand }
    };

    // ── Translation helper ────────────────────────────────────
    const t = useCallback((key) => translations[key] || key, [translations]);

    // ── Updated transaction type options with Supplier Due ───
    const transactionTypeOptions = useMemo(() => [
        { value: 'customer_due', label: translations.customerDue || 'Customer Due' },
        { value: 'customer_payment', label: translations.customerPayment || 'Customer Payment' },
        { value: 'supplier_due', label: translations.supplierDue || 'Supplier Due' },
        { value: 'supplier_payment', label: translations.supplierPayment || 'Supplier Payment' },
        { value: 'employee_salary', label: translations.salaryPayment || 'Salary Payment' },
        { value: 'expense', label: translations.shopExpense || 'Shop Expense' }
    ], [translations]);

    const transactionTypeMeta = {
        customer_due: { icon: faUsers, tone: 'warning' },
        customer_payment: { icon: faUsers, tone: 'success' },
        supplier_due: { icon: faTruck, tone: 'warning' },
        supplier_payment: { icon: faTruck, tone: 'info' },
        employee_salary: { icon: faUserTie, tone: 'primary' },
        expense: { icon: faReceipt, tone: 'danger' },
        purchase: { icon: faShoppingCart, tone: 'info' },
        sale: { icon: faCashRegister, tone: 'success' },
        return: { icon: faUndo, tone: 'warning' }
    };

    const paymentMethodOptions = useMemo(() => [
        { value: 'cash', label: translations.cash || 'Cash' },
        { value: 'bank_transfer', label: translations.bankTransfer || 'Bank Transfer' },
        { value: 'cheque', label: translations.cheque || 'Cheque' },
        { value: 'credit_card', label: translations.creditCard || 'Credit Card' },
        { value: 'mobile_payment', label: translations.mobilePayment || 'Mobile Payment' }
    ], [translations]);

    const filterTransactionTypeOptions = useMemo(() => [
        { value: '', label: translations.allTypes || 'All Types' },
        { value: 'sale', label: translations.sales || 'Sales' },
        { value: 'due', label: translations.customerDue || 'Customer Due' },
        { value: 'payment_received', label: translations.customerPayment || 'Customer Payment' },
        { value: 'payment_to_supplier', label: translations.supplierPayment || 'Supplier Payment' },
        { value: 'supplier_due', label: translations.supplierDue || 'Supplier Due' },
        { value: 'salary_payment', label: translations.salaryPayment || 'Salary Payment' },
        { value: 'expense', label: translations.shopExpense || 'Shop Expense' },
        { value: 'return', label: translations.returns || 'Return' },
        { value: 'purchase', label: translations.purchases || 'Purchase' }
    ], [translations]);

    const filterStatusOptions = useMemo(() => [
        { value: '', label: translations.showAll || 'Show All' },
        { value: 'completed', label: translations.completed || 'Completed' },
        { value: 'paid', label: translations.paid || 'Paid' },
        { value: 'pending', label: translations.pending || 'Pending' },
        { value: 'cancelled', label: translations.cancelled || 'Cancelled' }
    ], [translations]);

    // ── Toast ─────────────────────────────────────────────────
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        setToast({ show: true, type, message, progress: 100 });
        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            setToast(prev => ({ ...prev, progress: Math.max(0, (timeLeft / 3) * 100) }));
            if (timeLeft <= 0) { clearInterval(timer); setToast(prev => ({ ...prev, show: false })); }
        }, 100);
        toastTimerRef.current = timer;
    }, []);

    // ── Abort helper ──────────────────────────────────────────
    const cancelRequests = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
    }, []);

    // ── Theme ─────────────────────────────────────────────────
    const applyThemeToDocument = useCallback((color) => {
        if (!color || !/^#[0-9A-F]{6}$/i.test(color)) color = '#10b981';
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        document.documentElement.style.setProperty('--oztransui-primary', color);
        document.documentElement.style.setProperty('--oztransui-primary-rgb', `${r}, ${g}, ${b}`);
        setCurrentThemeColor(color);
    }, []);

    useEffect(() => {
        applyThemeToDocument(localStorage.getItem('ourzest_theme') || '#10b981');
        const onStorage = (e) => { if (e.key === 'ourzest_theme') applyThemeToDocument(e.newValue || '#10b981'); };
        const onTheme = (e) => { if (e.detail?.theme) applyThemeToDocument(e.detail.theme); };
        window.addEventListener('storage', onStorage);
        window.addEventListener('themeChanged', onTheme);
        const interval = setInterval(() => {
            const t = localStorage.getItem('ourzest_theme') || '#10b981';
            if (t !== currentThemeColor) applyThemeToDocument(t);
        }, 1000);
        return () => { window.removeEventListener('storage', onStorage); window.removeEventListener('themeChanged', onTheme); clearInterval(interval); };
    }, [applyThemeToDocument, currentThemeColor]);

    // ── Dark mode ─────────────────────────────────────────────
    useEffect(() => {
        const sync = () => {
            const isDark = localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(isDark);
            document.body.classList.toggle('oztransui-dark', isDark);
            document.querySelector('.oztransui-container')?.classList.toggle('oztransui-dark', isDark);
        };
        sync();
        const onStorage = (e) => { if (e.key === 'ourzest_darkMode') sync(); };
        const onToggle = (e) => {
            const isDark = e.detail?.darkMode ?? localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(isDark);
            document.querySelector('.oztransui-container')?.classList.toggle('oztransui-dark', isDark);
        };
        window.addEventListener('storage', onStorage);
        window.addEventListener('darkModeToggled', onToggle);
        const interval = setInterval(() => {
            const stored = localStorage.getItem('ourzest_darkMode') === 'true';
            if (stored !== darkMode) sync();
        }, 1000);
        return () => { window.removeEventListener('storage', onStorage); window.removeEventListener('darkModeToggled', onToggle); clearInterval(interval); };
    }, [darkMode]);

    // ── Language ──────────────────────────────────────────────
    useEffect(() => {
        const loadTrans = async (lang) => {
            const trans = await transactionsTranslator.getTranslations(lang);
            setTranslations(trans);
        };
        const onStorage = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) { setCurrentLanguage(lang); loadTrans(lang); }
        };
        const onLangChange = (e) => {
            const newLang = e.detail?.language;
            if (newLang && newLang !== currentLanguage) { setCurrentLanguage(newLang); loadTrans(newLang); }
        };
        loadTrans(currentLanguage);
        window.addEventListener('storage', onStorage);
        window.addEventListener('languageChanged', onLangChange);
        const interval = setInterval(() => {
            const stored = localStorage.getItem('ourzest_language') || 'en';
            if (stored !== currentLanguage) { setCurrentLanguage(stored); loadTrans(stored); }
        }, 1000);
        return () => { window.removeEventListener('storage', onStorage); window.removeEventListener('languageChanged', onLangChange); clearInterval(interval); };
    }, [currentLanguage]);

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
    useEffect(() => {
        const container = document.querySelector('.oztransui-container');
        container?.classList.toggle('oztransui-rtl', isRTL);
        container?.classList.toggle('oztransui-ltr', !isRTL);
        document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
        document.body.dir = isRTL ? 'rtl' : 'ltr';
    }, [isRTL]);

    // ── Check if ledger view ──────────────────────────────────
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const isLedger = params.get('action') === 'view_ledger';
        setIsLedgerView(isLedger);

        if (isLedger && currentShop) {
            // Apply filters from URL
            const urlParams = new URLSearchParams(window.location.search);
            const filtersFromUrl = {
                start_date: urlParams.get('start_date') || '',
                end_date: urlParams.get('end_date') || '',
                transaction_type: urlParams.get('transaction_type') || '',
                status: urlParams.get('status') || '',
                search: urlParams.get('search') || ''
            };
            setFilters(prev => ({ ...prev, ...filtersFromUrl }));

            // Set tab from URL
            const tab = urlParams.get('tab');
            if (tab && tab !== 'all') {
                setActiveTab(tab);
            }
        }
    }, [currentShop]);

    // ── Modal drag/resize ─────────────────────────────────────
    const centerModal = useCallback(() => {
        if (!modalRef.current) return;
        const rect = modalRef.current.getBoundingClientRect();
        const left = Math.max(0, (window.innerWidth - rect.width) / 2);
        const top = Math.max(0, (window.innerHeight - rect.height) / 2);
        modalRef.current.style.left = `${left}px`;
        modalRef.current.style.top = `${top}px`;
        modalRef.current.style.transform = 'none';
        setModalPosition({ x: left, y: top });
    }, []);

    const setWidthMode = useCallback((mode) => {
        if (!modalRef.current) return;
        setCurrentWidthMode(mode);
        const w = widthModes[mode].width;
        modalRef.current.style.width = typeof w === 'number' ? `${w}px` : w;
        modalRef.current.style.height = 'auto';
        setTimeout(() => centerModal(), 10);
    }, [centerModal]);

    const startDrag = useCallback((e) => {
        if (!modalRef.current) return;
        if (e.target.closest('button, input, select, textarea, a, .oztransui-width-btn, .oztransui-modal-close')) return;
        e.preventDefault();
        setIsDragging(true);
        const rect = modalRef.current.getBoundingClientRect();
        dragStart.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        modalRef.current.style.transition = 'none';
    }, []);

    const startResize = useCallback((e, type) => {
        e.preventDefault(); e.stopPropagation();
        if (!modalRef.current) return;
        setIsResizing(true);
        resizeType.current = type;
        const rect = modalRef.current.getBoundingClientRect();
        resizeStart.current = { x: e.clientX, y: e.clientY, width: rect.width, height: rect.height, left: rect.left, top: rect.top, minWidth: 350, minHeight: 400, maxWidth: window.innerWidth - 40, maxHeight: window.innerHeight - 40 };
        modalRef.current.style.transition = 'none';
    }, []);

    useEffect(() => {
        const onMove = (e) => {
            if (isDragging && modalRef.current) {
                let l = Math.max(0, Math.min(e.clientX - dragStart.current.x, window.innerWidth - modalRef.current.getBoundingClientRect().width));
                let tp = Math.max(0, Math.min(e.clientY - dragStart.current.y, window.innerHeight - modalRef.current.getBoundingClientRect().height));
                modalRef.current.style.left = `${l}px`;
                modalRef.current.style.top = `${tp}px`;
                modalRef.current.style.transform = 'none';
                setModalPosition({ x: l, y: tp });
            }
            if (isResizing && modalRef.current && resizeType.current) {
                const rs = resizeStart.current;
                const dx = e.clientX - rs.x, dy = e.clientY - rs.y;
                let nw = rs.width, nh = rs.height, nl = rs.left, nt = rs.top;
                const clampW = v => Math.min(rs.maxWidth, Math.max(rs.minWidth, v));
                const clampH = v => Math.min(rs.maxHeight, Math.max(rs.minHeight, v));
                switch (resizeType.current) {
                    case 'e': nw = clampW(rs.width + dx); break;
                    case 'w': nw = clampW(rs.width - dx); nl = rs.left + (rs.width - nw); break;
                    case 's': nh = clampH(rs.height + dy); break;
                    case 'n': nh = clampH(rs.height - dy); nt = rs.top + (rs.height - nh); break;
                    case 'ne': nw = clampW(rs.width + dx); nh = clampH(rs.height - dy); nt = rs.top + (rs.height - nh); break;
                    case 'nw': nw = clampW(rs.width - dx); nh = clampH(rs.height - dy); nl = rs.left + (rs.width - nw); nt = rs.top + (rs.height - nh); break;
                    case 'se': nw = clampW(rs.width + dx); nh = clampH(rs.height + dy); break;
                    case 'sw': nw = clampW(rs.width - dx); nh = clampH(rs.height + dy); nl = rs.left + (rs.width - nw); break;
                }
                Object.assign(modalRef.current.style, { width: `${nw}px`, height: `${nh}px`, left: `${nl}px`, top: `${nt}px` });
                setModalSize({ width: nw, height: nh });
                setModalPosition({ x: nl, y: nt });
            }
        };
        const onUp = () => {
            if (isDragging || isResizing) {
                setIsDragging(false); setIsResizing(false); resizeType.current = null;
                if (modalRef.current) modalRef.current.style.transition = '';
            }
        };
        if (isDragging || isResizing) {
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        }
        return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    }, [isDragging, isResizing]);

    useEffect(() => {
        if (showAddModal && modalRef.current && !isModalInitialized) {
            setTimeout(() => { centerModal(); setWidthMode('medium'); setIsModalInitialized(true); }, 50);
        }
        if (!showAddModal) setIsModalInitialized(false);
    }, [showAddModal, centerModal, setWidthMode, isModalInitialized]);

    useEffect(() => {
        const onResize = () => {
            if (!modalRef.current || !showAddModal) return;
            const rect = modalRef.current.getBoundingClientRect();
            const l = Math.max(0, Math.min(rect.left, window.innerWidth - rect.width));
            const tp = Math.max(0, Math.min(rect.top, window.innerHeight - rect.height));
            if (l !== rect.left || tp !== rect.top) { modalRef.current.style.left = `${l}px`; modalRef.current.style.top = `${tp}px`; }
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [showAddModal]);

    useEffect(() => { if (showAddModal && firstInputRef.current) setTimeout(() => firstInputRef.current?.focus(), 150); }, [showAddModal]);
    useEffect(() => () => { if (toastTimerRef.current) clearInterval(toastTimerRef.current); }, []);

    // ─────────────────────────────────────────────────────────
    // 📡 API — stable callbacks
    // ─────────────────────────────────────────────────────────
    const loadBusinessInfo = useCallback(async () => {
        if (!user?.username) return;
        try {
            const res = await fetch(`https://ourzestapp.online/oz_backend/root_users.php?action=get_business_info&username=${encodeURIComponent(user.username)}`, { credentials: 'include' });
            const data = await res.json();
            if (data.status === 'success') setBusinessInfo(data);
            else setBusinessInfo({ businessname: user.business_name || user.fullname || '', businessaddress: user.business_address || '', phonenumber: user.phonenumber || '', email: user.email || '' });
        } catch { setBusinessInfo({ businessname: user?.business_name || user?.fullname || '' }); }
    }, [user]);

    const loadCustomers = useCallback(async () => {
        if (!currentShop) return;
        try {
            const res = await fetch(CUSTOMERS_API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'get_records', shop_id: currentShop.shop_id }) });
            const data = await res.json();
            if (data.status === 'success') setCustomers(data.data || []);
        } catch { /* ignore */ }
    }, [currentShop]);

    const loadSuppliers = useCallback(async () => {
        if (!currentShop) return;
        try {
            const res = await fetch(TRANSACTIONS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_all_suppliers', shop_id: currentShop.shop_id })
            });
            const data = await res.json();
            if (data.status === 'success') {
                setSuppliers(data.data || []);
            } else {
                const fallbackRes = await fetch(SUPPLIERS_API, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include'
                });
                const fallbackData = await fallbackRes.json();
                if (fallbackData.status === 'success') {
                    setSuppliers(fallbackData.data || []);
                }
            }
        } catch (err) {
            console.warn('Failed to load suppliers:', err);
            setSuppliers([]);
        }
    }, [currentShop]);

    const loadEmployees = useCallback(async () => {
        if (!currentShop) return;
        try {
            const res = await fetch(EMPLOYEES_API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'get_records', shop_id: currentShop.shop_id }) });
            const data = await res.json();
            if (data.status === 'success') setEmployees(data.data || []);
        } catch { /* ignore */ }
    }, [currentShop]);

    // ── Load products for profit calculation ──────────────────
    // ── Load products for profit calculation ──────────────────
const loadProducts = useCallback(async () => {
    if (!currentShop) return;
    try {
        const res = await fetch(PRODUCTS_API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                action: 'get_records',
                shop_id: currentShop.shop_id,
                username: user?.username
            })
        });
        const data = await res.json();
        if (data.status === 'success') {
            // Filter products to ensure they belong to the current shop
            const shopProducts = (data.data || []).filter(p => 
                parseInt(p.shop_id) === parseInt(currentShop.shop_id)
            );
            setProductsData(shopProducts);
        } else {
            setProductsData([]);
        }
    } catch (err) {
        console.warn('Failed to load products:', err);
        setProductsData([]);
    }
}, [currentShop, user]);

    // ── Load unpaid invoices for supplier ─────────────────────
    const loadSupplierUnpaidInvoices = useCallback(async (supplierId) => {
        if (!currentShop || !supplierId) {
            setSupplierUnpaidInvoices([]);
            return;
        }

        try {
            const res = await fetch(TRANSACTIONS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_supplier_unpaid_invoices',
                    shop_id: currentShop.shop_id,
                    supplier_id: supplierId
                })
            });
            const data = await res.json();
            if (data.status === 'success') {
                setSupplierUnpaidInvoices(data.data || []);
            } else {
                setSupplierUnpaidInvoices([]);
            }
        } catch (err) {
            console.warn('Failed to load supplier unpaid invoices:', err);
            setSupplierUnpaidInvoices([]);
        }
    }, [currentShop]);

    // ── Handle supplier selection for payment ─────────────────
    const handleSupplierSelectForPayment = useCallback((supplierId) => {
        setSelectedSupplierForPayment(supplierId);
        setSelectedInvoiceForPayment(null);
        setPaymentAmount('');
        setIsFullPayment(true);
        if (supplierId) {
            loadSupplierUnpaidInvoices(supplierId);
        } else {
            setSupplierUnpaidInvoices([]);
        }
    }, [loadSupplierUnpaidInvoices]);

    // ── Handle invoice selection for payment ─────────────────
    const handleInvoiceSelectForPayment = useCallback((invoice) => {
        if (!invoice) {
            setSelectedInvoiceForPayment(null);
            setPaymentAmount('');
            return;
        }
        setSelectedInvoiceForPayment(invoice);
        const dueAmount = parseFloat(invoice.amount_due || invoice.balance || 0);
        setPaymentAmount(dueAmount.toString());
        setIsFullPayment(true);
    }, []);

    // ── Handle payment amount change ──────────────────────────
    const handlePaymentAmountChange = useCallback((value) => {
        setPaymentAmount(value);
        if (selectedInvoiceForPayment) {
            const dueAmount = parseFloat(selectedInvoiceForPayment.amount_due || selectedInvoiceForPayment.balance || 0);
            const enteredAmount = parseFloat(value) || 0;
            setIsFullPayment(enteredAmount >= dueAmount);
        }
    }, [selectedInvoiceForPayment]);

    // ── STABLE load transactions — reads state via refs ───────
 const loadAllTransactions = useCallback(async () => {
    if (!currentShop) return;

    cancelRequests();
    abortControllerRef.current = new AbortController();
    setLoading(true);

    const currentFilters = filtersRef.current;
    const currentTab = activeTabRef.current;

    try {
        const body = {
            action: 'get_transactions',
            shop_id: currentShop.shop_id,
            limit: 1000,
            offset: 0
        };

        // Tab-specific type filter
        const tabTypeMap = {
            sales: 'sale',
            salary: 'salary_payment',
            expense: 'expense',
            purchases: 'purchase',
            returns: 'return'
        };
        if (tabTypeMap[currentTab]) {
            body.transaction_type = tabTypeMap[currentTab];
        } else if (currentTab === 'all' && currentFilters.transaction_type) {
            body.transaction_type = currentFilters.transaction_type;
        }

        if (currentFilters.start_date) body.start_date = currentFilters.start_date;
        if (currentFilters.end_date) body.end_date = currentFilters.end_date;
        if (currentFilters.status) body.status = currentFilters.status;
        if (currentFilters.search) body.search = currentFilters.search;

        const res = await fetch(TRANSACTIONS_API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
            signal: abortControllerRef.current.signal
        });

        const data = await res.json();

        if (data.status === 'success') {
            const all = data.data || [];

            // ── Sort transactions so purchases come before payments ──
            const sortedTransactions = [...all].sort((a, b) => {
                // Same invoice number - purchases must come before payments
                if (a.invoice_number && b.invoice_number &&
                    a.invoice_number === b.invoice_number) {

                    if (a.transaction_type === 'purchase' &&
                        b.transaction_type === 'payment_to_supplier') {
                        return -1;
                    }

                    if (a.transaction_type === 'payment_to_supplier' &&
                        b.transaction_type === 'purchase') {
                        return 1;
                    }
                }

                // Sort by date
                const d1 = new Date(a.created_at || a.transaction_date || 0);
                const d2 = new Date(b.created_at || b.transaction_date || 0);

                if (d1.getTime() !== d2.getTime()) {
                    return d1 - d2;
                }

                // If same date, sort by ID
                return (parseInt(a.id) || 0) - (parseInt(b.id) || 0);
            });

            // ── First pass: Build invoice map with grand totals and original paid ──
            const invoiceMap = {};
            sortedTransactions.forEach(tx => {
                if (tx.transaction_type === 'purchase' && tx.invoice_number) {
                    const invNum = tx.invoice_number;
                    if (!invoiceMap[invNum]) {
                        // ✅ Track original paid amount from purchase record
                        const originalPaid = parseFloat(tx.amount_paid || tx.paid_amount || 0);
                        const grandTotal = parseFloat(tx.grand_total || tx.amount || 0);
                        
                        invoiceMap[invNum] = {
                            invoice_number: invNum,
                            grand_total: grandTotal,
                            original_paid: originalPaid,
                            original_balance: grandTotal - originalPaid,
                            payments: []
                        };
                    }
                }
            });

            // ── Second pass: Collect payments against invoices ──
            sortedTransactions.forEach(tx => {
                if (tx.transaction_type === 'payment_to_supplier') {
                    // Try to match by invoice_number
                    if (tx.invoice_number && invoiceMap[tx.invoice_number]) {
                        const amount = parseFloat(tx.amount || 0);
                        invoiceMap[tx.invoice_number].payments.push({
                            ...tx,
                            amount: amount
                        });
                    }
                    // Try to match by purchase_id
                    else if (tx.purchase_id) {
                        // Find matching purchase
                        const matchingInvoice = sortedTransactions.find(t =>
                            t.transaction_type === 'purchase' &&
                            (t.transaction_id === tx.purchase_id || t.id === tx.purchase_id)
                        );
                        if (matchingInvoice && matchingInvoice.invoice_number && invoiceMap[matchingInvoice.invoice_number]) {
                            const amount = parseFloat(tx.amount || 0);
                            invoiceMap[matchingInvoice.invoice_number].payments.push({
                                ...tx,
                                amount: amount
                            });
                        }
                    }
                }
            });

            // ── Third pass: Process transactions with cascading balance ──
            // Track running balance per invoice
            const runningBalance = {};

            const updatedData = sortedTransactions.map(tx => {
                // ============================================================
                // ✅ FIX: PURCHASE TRANSACTION - KEEP ORIGINAL PAID AMOUNT
                // The purchase row shows:
                //   grand_total = original amount
                //   amount_paid = original paid amount (from purchase form)
                //   balance = original balance (grand_total - original_paid)
                // ============================================================
                if (tx.transaction_type === 'purchase' && tx.invoice_number) {
                    const invNum = tx.invoice_number;
                    const grandTotal = parseFloat(tx.grand_total || tx.amount || 0);
                    
                    // ✅ Get original values from invoiceMap
                    const originalPaid = invoiceMap[invNum]?.original_paid || 0;
                    const originalBalance = invoiceMap[invNum]?.original_balance || grandTotal;

                    // Initialize running balance for this invoice
                    // ✅ Start with original balance (grand_total - original_paid)
                    runningBalance[invNum] = originalBalance;

                    // Determine status based on original values
                    let status = 'pending';
                    let paymentStatus = 'pending';
                    if (originalBalance <= 0.01) {
                        status = 'completed';
                        paymentStatus = 'paid';
                    } else if (originalPaid > 0) {
                        status = 'partial';
                        paymentStatus = 'partial';
                    }

                    // ✅ KEEP ORIGINAL PAID AMOUNT - DON'T SET TO 0
                    return {
                        ...tx,
                        grand_total: grandTotal,
                        amount_paid: originalPaid,        // ✅ ORIGINAL - NOT 0
                        paid_amount: originalPaid,        // ✅ ORIGINAL - NOT 0
                        amount_due: originalBalance,      // ✅ ORIGINAL BALANCE
                        balance: originalBalance,         // ✅ ORIGINAL BALANCE
                        status: status,
                        payment_status: paymentStatus,
                        _payments: invoiceMap[invNum]?.payments || [],
                        _running_balance: runningBalance[invNum],
                        _original_paid: originalPaid,
                        _original_balance: originalBalance,
                        _is_purchase_row: true
                    };
                }

                // ============================================================
                // SUPPLIER PAYMENT - Shows cascading balance
                // Each payment shows the balance AFTER this payment
                // ============================================================
                if (tx.transaction_type === 'payment_to_supplier') {
                    let invNum = tx.invoice_number;
                    let payment = parseFloat(tx.amount || 0);

                    // If no invoice_number, try to find matching purchase
                    if (!invNum && tx.purchase_id) {
                        const matchingPurchase = sortedTransactions.find(t =>
                            t.transaction_type === 'purchase' &&
                            (t.transaction_id === tx.purchase_id || t.id === tx.purchase_id)
                        );
                        if (matchingPurchase) {
                            invNum = matchingPurchase.invoice_number;
                        }
                    }

                    // Get current balance before this payment
                    let currentBalance = runningBalance[invNum];

                    // If balance not tracked yet, initialize from invoiceMap
                    if (currentBalance === undefined && invNum && invoiceMap[invNum]) {
                        currentBalance = invoiceMap[invNum].original_balance || invoiceMap[invNum].grand_total || 0;
                        runningBalance[invNum] = currentBalance;
                    }

                    // Calculate new balance after this payment
                    let newBalance = Math.max((currentBalance || 0) - payment, 0);

                    // Update running balance for next payment
                    if (invNum) {
                        runningBalance[invNum] = newBalance;
                    }

                    // ✅ Payment transactions show the balance BEFORE as grand_total
                    // and the balance AFTER as amount_due/balance
                    return {
                        ...tx,
                        // Grand Total shows the balance BEFORE this payment
                        grand_total: currentBalance || payment,
                        // Paid amount is this payment
                        amount_paid: payment,
                        paid_amount: payment,
                        // Balance due is the remaining balance AFTER this payment
                        amount_due: newBalance,
                        balance: newBalance,
                        status: 'paid',
                        payment_status: 'paid',
                        _invoice_number: invNum,
                        _current_balance_before: currentBalance,
                        _balance_after: newBalance,
                        _is_payment_row: true
                    };
                }

                // For all other transaction types, keep as is
                return tx;
            });

            // ── Update all state ──
            setTransactions(updatedData);
            setAllTransactionsForPL(updatedData);
            setFilteredTransactions(updatedData);
            setSalesData(updatedData.filter(t => t.transaction_type === 'sale'));
            setPurchasesData(updatedData.filter(t => t.transaction_type === 'purchase'));
            setReturnsData(updatedData.filter(t => t.transaction_type === 'return'));
            setSalaryData(updatedData.filter(t => t.transaction_type === 'salary_payment'));
            setExpensesData(updatedData.filter(t => t.transaction_type === 'expense'));

            // ── Recalculate summary ──
            let totalSales = 0;
            let totalPaymentsReceived = 0;
            let totalExpenses = 0;
            let totalPaymentsToSuppliers = 0;
            let totalSalaryPayments = 0;

            updatedData.forEach(tx => {
                const originalAmount = parseFloat(tx.grand_total || tx.amount || 0);
                const paid = parseFloat(tx.amount_paid || tx.paid_amount || 0);

                switch (tx.transaction_type) {
                    case 'sale':
                        totalSales += originalAmount;
                        break;
                    case 'payment_received':
                        totalPaymentsReceived += paid;
                        break;
                    case 'expense':
                        totalExpenses += originalAmount;
                        break;
                    case 'payment_to_supplier':
                        totalPaymentsToSuppliers += originalAmount;
                        break;
                    case 'salary_payment':
                        totalSalaryPayments += originalAmount;
                        break;
                    default:
                        break;
                }
            });

            const netBalance = totalSales - totalExpenses - totalPaymentsToSuppliers - totalSalaryPayments;

            setSummary({
                total_sales: totalSales,
                total_payments_received: totalPaymentsReceived,
                total_expenses: totalExpenses,
                total_payments_to_suppliers: totalPaymentsToSuppliers,
                total_salary_payments: totalSalaryPayments,
                net_balance: netBalance
            });

            if (data.pagination) setPagination(prev => ({ ...prev, total: data.pagination.total, totalPages: data.pagination.total_pages }));
        } else {
            showToast('error', data.message || 'Failed to load transactions');
        }
    } catch (err) {
        if (err.name !== 'AbortError') showToast('error', 'Failed to load transactions');
    } finally {
        setLoading(false);
        abortControllerRef.current = null;
    }
}, [currentShop, cancelRequests, showToast]);
    // ── Client-side search filter ──────────────────────────────
    useEffect(() => {
        // Only run client-side filtering if we have transactions
        if (transactions.length === 0) return;

        // Get the search term from filters
        const searchTerm = filters.search?.trim().toLowerCase() || '';

        // If no search term, just use all transactions
        if (!searchTerm) {
            setFilteredTransactions(transactions);
            setSalesData(transactions.filter(t => t.transaction_type === 'sale'));
            setPurchasesData(transactions.filter(t => t.transaction_type === 'purchase'));
            setReturnsData(transactions.filter(t => t.transaction_type === 'return'));
            setSalaryData(transactions.filter(t => t.transaction_type === 'salary_payment'));
            setExpensesData(transactions.filter(t => t.transaction_type === 'expense'));
            return;
        }

        // Filter transactions client-side
        const filtered = transactions.filter(tx => {
            // Search in reference_name
            const referenceName = (tx.reference_name || '').toLowerCase();
            // Search in description
            const description = (tx.description || '').toLowerCase();
            // Also search in invoice_number if available
            const invoiceNumber = (tx.invoice_number || '').toLowerCase();
            // Also search in customer/supplier/employee name from transaction
            const customerName = (tx.customer_name || '').toLowerCase();
            const supplierName = (tx.supplier_name || '').toLowerCase();
            const employeeName = (tx.employee_name || '').toLowerCase();

            return referenceName.includes(searchTerm) ||
                   description.includes(searchTerm) ||
                   invoiceNumber.includes(searchTerm) ||
                   customerName.includes(searchTerm) ||
                   supplierName.includes(searchTerm) ||
                   employeeName.includes(searchTerm);
        });

        // Update filtered transactions
        setFilteredTransactions(filtered);
        setSalesData(filtered.filter(t => t.transaction_type === 'sale'));
        setPurchasesData(filtered.filter(t => t.transaction_type === 'purchase'));
        setReturnsData(filtered.filter(t => t.transaction_type === 'return'));
        setSalaryData(filtered.filter(t => t.transaction_type === 'salary_payment'));
        setExpensesData(filtered.filter(t => t.transaction_type === 'expense'));

    }, [transactions, filters.search]);

    // ─────────────────────────────────────────────────────────
    // 🔄 SINGLE UNIFIED EFFECT
    // ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (!currentShop) return;

        const timer = setTimeout(() => {
            loadAllTransactions();
        }, 300);

        return () => {
            clearTimeout(timer);
            cancelRequests();
        };
    }, [
        currentShop,
        activeTab,
        filters.start_date,
        filters.end_date,
        filters.status,
        filters.transaction_type,
        filters.search,
        loadAllTransactions,
        cancelRequests,
    ]);

    // Load supporting data once on shop change
    useEffect(() => {
        if (!currentShop) return;
        loadCustomers();
        loadSuppliers();
        loadEmployees();
        loadBusinessInfo();
        loadProducts(); // Load products for profit calculation
    }, [currentShop, loadCustomers, loadSuppliers, loadEmployees, loadBusinessInfo, loadProducts]);

    // Cleanup on unmount
    useEffect(() => () => cancelRequests(), [cancelRequests]);

    // ── Tab change — just sets state, effect handles the load ─
    const handleTabChange = useCallback((tab) => {
        if (activeTabRef.current === tab) return;
        cancelRequests();
        setActiveTab(tab);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
        if (tab !== 'all') setFilters(prev => ({ ...prev, transaction_type: '' }));
    }, [cancelRequests]);

    // ── Refresh ───────────────────────────────────────────────
    const handleRefresh = useCallback(async () => {
        setRefreshing(true);
        try {
            await loadAllTransactions();
            await loadProducts(); // Refresh products too
            showToast('success', 'Data refreshed successfully');
        } finally {
            setRefreshing(false);
        }
    }, [loadAllTransactions, loadProducts, showToast]);

    // ── Reset filters — just set state, effect fires ──────────
    const resetFilters = useCallback(() => {
        setFilters({ start_date: '', end_date: '', transaction_type: '', status: '', search: '' });
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    }, []);

    const resetForm = useCallback(() => {
        setFormData({ reference_id: '', amount: '', transaction_date: new Date().toISOString().split('T')[0], description: '', payment_method: 'cash', invoice_number: '', expense_category: '', expense_name: '', vendor_name: '', salary_type: 'salary' });
        setSelectedSupplierForPayment(null);
        setSelectedInvoiceForPayment(null);
        setPaymentAmount('');
        setIsFullPayment(true);
        setSupplierUnpaidInvoices([]);
    }, []);

    // ── View profile ──────────────────────────────────────────
    const handleViewProfile = useCallback(async (type, id) => {
        if (!id) { showToast('error', 'Invalid profile ID'); return; }
        setProfileType(type);
        setLoading(true);
        try {
            const actionMap = { customer: 'get_customer_profile', supplier: 'get_supplier_profile', employee: 'get_employee_profile' };
            const keyMap = { customer: 'customer_id', supplier: 'supplier_id', employee: 'employee_id' };
            if (!actionMap[type]) { showToast('error', 'Invalid profile type'); return; }
            const res = await fetch(TRANSACTIONS_API, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: actionMap[type], shop_id: currentShop?.shop_id, [keyMap[type]]: id })
            });
            const data = await res.json();
            if (data.status === 'success') {
                setSelectedProfile({ ...data.data.profile, summary: data.data.summary, type });
                setProfileTransactions(data.data.transactions || []);
                setShowProfileModal(true);
            } else showToast('error', data.message || 'Failed to load profile');
        } catch { showToast('error', 'Failed to load profile'); }
        finally { setLoading(false); }
    }, [currentShop, showToast]);

    // ── Add transaction ───────────────────────────────────────
    const handleAddTransaction = useCallback(async () => {
        if (!currentShop) { showToast('error', 'Select a shop first'); return; }

        // Validation
        if (transactionType === 'supplier_payment') {
            if (!selectedSupplierForPayment) {
                showToast('error', 'Please select a supplier');
                return;
            }
            if (!selectedInvoiceForPayment && !formData.amount) {
                showToast('error', 'Please select an invoice or enter a payment amount');
                return;
            }
            if (!formData.amount || parseFloat(formData.amount) <= 0) {
                showToast('error', 'Please enter a valid payment amount');
                return;
            }
        } else if (transactionType === 'supplier_due') {
            if (!formData.reference_id) {
                showToast('error', 'Please select a supplier');
                return;
            }
            if (!formData.amount || parseFloat(formData.amount) <= 0) {
                showToast('error', 'Please enter a valid amount');
                return;
            }
        } else if (transactionType !== 'expense') {
            if (!formData.reference_id) {
                showToast('error', `Please select a ${transactionType.replace('_', ' ')}`);
                return;
            }
            if (!formData.amount || parseFloat(formData.amount) <= 0) {
                showToast('error', 'Please enter a valid amount');
                return;
            }
        } else {
            if (!formData.amount || parseFloat(formData.amount) <= 0) {
                showToast('error', 'Please enter a valid amount');
                return;
            }
            if (!formData.expense_name) {
                showToast('error', 'Please enter expense name');
                return;
            }
        }

        setLoading(true);
        try {
            const actionMap = {
                customer_due: 'add_due',
                customer_payment: 'add_payment',
                supplier_due: 'add_supplier_due',
                supplier_payment: 'add_supplier_payment',
                employee_salary: 'add_employee_salary',
                expense: 'add_expense'
            };
            const refKeyMap = {
                customer_due: 'customer_id',
                customer_payment: 'customer_id',
                supplier_due: 'supplier_id',
                supplier_payment: 'supplier_id',
                employee_salary: 'employee_id'
            };
            const action = actionMap[transactionType];
            if (!action) throw new Error('Invalid transaction type');

            const body = {
                action,
                shop_id: currentShop.shop_id,
                username: user?.username,
                transaction_date: formData.transaction_date,
                payment_method: formData.payment_method,
                description: formData.description,
                amount: parseFloat(formData.amount)
            };

            // Handle supplier payment special fields
            if (transactionType === 'supplier_payment') {
                body.supplier_id = parseInt(selectedSupplierForPayment);
                if (selectedInvoiceForPayment) {
                    body.invoice_id = selectedInvoiceForPayment.transaction_id;
                    body.invoice_number = selectedInvoiceForPayment.invoice_number;
                    body.is_full_payment = isFullPayment ? 1 : 0;
                }
                if (formData.invoice_number) {
                    body.invoice_number = formData.invoice_number;
                }
            } else if (refKeyMap[transactionType]) {
                body[refKeyMap[transactionType]] = formData.reference_id;
            }

            if (transactionType === 'expense') {
                body.expense_name = formData.expense_name;
                body.expense_category = formData.expense_category || 'General';
            }

            if (transactionType === 'supplier_due') {
                body.invoice_number = formData.invoice_number || `SUP-DUE-${Date.now()}`;
            }

            const res = await fetch(TRANSACTIONS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const data = await res.json();

            if (data.status === 'success') {
                showToast('success', data.message || 'Transaction recorded successfully');
                setShowAddModal(false);
                resetForm();
                await loadAllTransactions();
                await loadProducts(); // Refresh products after transaction
            } else {
                showToast('error', data.message || 'Failed to add transaction');
            }
        } catch (err) {
            showToast('error', err.message || 'Failed to add transaction');
        } finally {
            setLoading(false);
        }
    }, [currentShop, formData, transactionType, user, showToast, resetForm, loadAllTransactions, loadProducts, selectedSupplierForPayment, selectedInvoiceForPayment, isFullPayment]);

    // ── View Ledger ───────────────────────────────────────────
    const openLedger = useCallback(() => {
        if (!currentShop) {
            showToast('error', 'Please select a shop first');
            return;
        }

        const shopId = currentShop.shop_id;
        const username = user?.username || '';
        const baseUrl = window.location.origin;

        // Build query params with current filters
        const params = new URLSearchParams({
            shop_id: shopId,
            username: username,
            action: 'view_ledger'
        });

        // Add filters if present
        if (filters.start_date) params.append('start_date', filters.start_date);
        if (filters.end_date) params.append('end_date', filters.end_date);
        if (filters.transaction_type) params.append('transaction_type', filters.transaction_type);
        if (filters.status) params.append('status', filters.status);
        if (filters.search) params.append('search', filters.search);
        if (activeTab !== 'all') params.append('tab', activeTab);

        // Add business info
        if (businessInfo?.businessname) params.append('business_name', businessInfo.businessname);
        if (businessInfo?.businessaddress) params.append('business_address', businessInfo.businessaddress);
        if (currentShop?.shop_name) params.append('shop_name', currentShop.shop_name);

        // Open in new tab - using the ledger route
        window.open(`${baseUrl}/ledger?${params.toString()}`, '_blank');
    }, [currentShop, user, filters, activeTab, businessInfo, showToast]);

    // ── Receipt ───────────────────────────────────────────────
    const handleOpenReceipt = useCallback((transaction) => {
        printTransactionReceipt(transaction, businessInfo, currentShop, currentThemeColor);
    }, [businessInfo, currentShop, currentThemeColor]);

    // ── Helpers ───────────────────────────────────────────────
    const formatCurrency = useCallback((amount) => new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0), []);
    const formatDate = useCallback((date) => { if (!date) return 'N/A'; return new Date(date).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }); }, []);
    const formatDateFull = useCallback((date) => { if (!date) return 'N/A'; return new Date(date).toLocaleString('en-IN', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }, []);

    // ── getTransactionTypeLabel ───────────────────────────────
    const getTransactionTypeLabel = useCallback((type, transaction = null) => {
        if (type === 'purchase') {
            return t('purchases') || 'Purchase';
        }

        if (type === 'payment_to_supplier' && transaction && transaction.purchase_id) {
            return t('purchases') || 'Purchase';
        }

        const map = {
            sale: t('sales') || 'Sale',
            due: t('customerDue') || 'Customer Due',
            payment_received: t('customerPayment') || 'Customer Payment',
            payment_to_supplier: t('supplierPayment') || 'Supplier Payment',
            supplier_due: t('supplierDue') || 'Supplier Due',
            salary_payment: t('salaryPayment') || 'Salary Payment',
            expense: t('shopExpense') || 'Shop Expense',
            return: t('returns') || 'Return',
            purchase: t('purchases') || 'Purchase'
        };
        return map[type] || type;
    }, [t]);

    // ── getTransactionTypeClass ───────────────────────────────
    const getTransactionTypeClass = (type) => {
        const map = {
            sale: 'type-sale',
            due: 'type-due',
            payment_received: 'type-payment-received',
            payment_to_supplier: 'type-payment-to-supplier',
            supplier_due: 'type-supplier-due',
            salary_payment: 'type-salary',
            expense: 'type-expense',
            return: 'type-return',
            purchase: 'type-purchase'
        };
        return map[type] || 'type-default';
    };

    const getStatusClass = (status) => {
        if (status === 'completed' || status === 'paid') return 'success';
        if (status === 'pending') return 'pending';
        if (status === 'cancelled') return 'cancelled';
        return 'default';
    };

    const getStatusLabel = (status) => {
        if (status === 'completed' || status === 'paid') return 'Paid';
        if (status === 'pending') return 'Pending';
        if (status === 'cancelled') return 'Cancelled';
        return status || 'Completed';
    };

    const getTabTitle = useCallback(() => {
        const map = { all: t('allTransactions'), sales: t('sales'), purchases: t('purchases'), returns: t('returns'), salary: t('salaries'), expense: t('expenses'), profit_loss: t('profitLoss') };
        return map[activeTab] || t('allTransactions');
    }, [activeTab, t]);

    const getCurrentTabData = useCallback(() => {
        switch (activeTab) {
            case 'all': return filteredTransactions;
            case 'sales': return salesData;
            case 'purchases': return purchasesData;
            case 'returns': return returnsData;
            case 'salary': return salaryData;
            case 'expense': return expensesData;
            case 'profit_loss': return allTransactionsForPL;
            default: return filteredTransactions;
        }
    }, [activeTab, filteredTransactions, salesData, purchasesData, returnsData, salaryData, expensesData, allTransactionsForPL]);

    // ─────────────────────────────────────────────────────────
    // 🎨 RENDER HELPERS
    // ─────────────────────────────────────────────────────────
    const renderTransactionsTable = (data) => (
        <div className="ozlistui-table-scroll">
            <div className="ozlistui-table-container">
                <div className="ozlistui-table-wrapper">
                    {data.length === 0 && !loading ? (
                        <div className="oztransui-no-data">
                            <FontAwesomeIcon icon={faReceipt} />
                            <p>{t('noTransactions')}</p>
                        </div>
                    ) : (
                        <table className="ozlistui-table">
                            <thead>
                                <tr>
                                    <th className="ozlistui-id-column ozlistui-centered-cell">{t('serialNo')}</th>
                                    <th className="ozlistui-centered-cell">{t('date')}</th>
                                    <th className="ozlistui-centered-cell">{t('type')}</th>
                                    <th className="ozlistui-centered-cell">{t('referenceName')}</th>
                                    <th className="ozlistui-centered-cell">{t('description')}</th>
                                    <th className="ozlistui-centered-cell">{t('grandTotal')}</th>
                                    <th className="ozlistui-centered-cell">{t('paidAmount')}</th>
                                    <th className="ozlistui-centered-cell">{t('balanceDue')}</th>
                                    <th className="ozlistui-centered-cell">{t('status')}</th>
                                    <th className="ozlistui-actions-column ozlistui-centered-cell">{t('actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((tx, index) => {
                                    // FIX: Use grand_total as the original amount, amount as fallback
                                    const originalAmount = parseFloat(tx.grand_total || tx.amount || 0);
                                    const paid = parseFloat(tx.amount_paid || tx.paid_amount || 0);
                                    // Balance = original amount - paid
                                    const balance = originalAmount - paid;
                                    // If amount_due or balance is provided, use it but ensure it's consistent
                                    const balanceFromApi = parseFloat(tx.amount_due || tx.balance || 0);
                                    const finalBalance = balanceFromApi > 0 ? balanceFromApi : (balance > 0 ? balance : 0);
                                    return (
                                        <tr key={tx.transaction_id || tx.id} className="oztransui-clickable-row" style={{ cursor: 'pointer' }}>
                                            <td className="ozlistui-id-cell ozlistui-centered-cell"><span className="ozlistui-cell-text">{index + 1}</span></td>
                                            <td className="ozlistui-centered-cell"><span className="ozlistui-cell-text">{formatDate(tx.transaction_date || tx.date)}</span></td>
                                            <td className="ozlistui-centered-cell">
                                                <span className={`oztransui-type-badge ${getTransactionTypeClass(tx.transaction_type)}`}>
                                                    {getTransactionTypeLabel(tx.transaction_type, tx)}
                                                </span>
                                            </td>
                                            <td className="ozlistui-centered-cell">
                                                {tx.reference_type && tx.reference_id ? (
                                                    <button className="oztransui-reference-link" onClick={(e) => { e.stopPropagation(); handleViewProfile(tx.reference_type, tx.reference_id); }}>
                                                        <span className="ozlistui-cell-text ozlistui-customer-name">{tx.reference_name || '-'}</span>
                                                    </button>
                                                ) : (
                                                    <span className="ozlistui-cell-text">{tx.reference_name || '-'}</span>
                                                )}
                                            </td>
                                            <td className="ozlistui-centered-cell"><span className="ozlistui-cell-text ozlistui-store-name">{tx.description || '-'}</span></td>
                                            <td className="ozlistui-centered-cell"><span className="ozlistui-cell-text ozlistui-amount-cell">Rs. {formatCurrency(originalAmount)}</span></td>
                                            <td className="ozlistui-centered-cell"><span className="ozlistui-cell-text ozlistui-amount-cell">Rs. {formatCurrency(paid)}</span></td>
                                            <td className="ozlistui-centered-cell"><span className="ozlistui-cell-text ozlistui-amount-cell" style={{ color: finalBalance > 0 ? '#d97706' : '#059669' }}>Rs. {formatCurrency(finalBalance)}</span></td>
                                            <td className="ozlistui-centered-cell">
                                                <span className={`oztransui-status-badge oztransui-status-${getStatusClass(tx.status || tx.payment_status)}`}>
                                                    {getStatusLabel(tx.status || tx.payment_status)}
                                                </span>
                                            </td>
                                            <td className="ozlistui-actions-cell ozlistui-centered-cell">
                                                <div className="ozlistui-actions-content">
                                                    <button className="ozlistui-action-btn ozlistui-print-btn" onClick={(e) => { e.stopPropagation(); handleOpenReceipt(tx); }} title={t('viewReceipt')}>
                                                        <FontAwesomeIcon icon={faReceipt} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );

    // ── UPDATED renderProfitLossTab ──────────────────────────
    // ── UPDATED renderProfitLossTab ──────────────────────────
const renderProfitLossTab = () => (
    <div className="oztransui-profit-loss-tab">
        <div className="oztransui-profit-loss-tab-header">
            <div className="oztransui-profit-loss-tab-title">
                <FontAwesomeIcon icon={faBalanceScale} />
                <h2>{t('profitLossStatement')}</h2>
            </div>
            <div className="oztransui-profit-loss-tab-actions">
                <button className="oztransui-btn-ledger" onClick={openLedger}>
                    <FontAwesomeIcon icon={faFileInvoice} /> 
                    <span>{t('viewLedger') || 'View Ledger'}</span>
                </button>
                <button className="oztransui-refresh-btn" onClick={handleRefresh}>
                    <FontAwesomeIcon icon={faRefresh} /> 
                    <span>{t('refresh')}</span>
                </button>
            </div>
        </div>
        <div className="oztransui-profit-loss-content">
            <ProfitLossStatement 
                data={allTransactionsForPL} 
                products={productsData}
                translations={translations} 
                formatCurrency={formatCurrency}
                shopId={currentShop?.shop_id}
            />
        </div>
    </div>
);
    const renderTabContent = () => {
        switch (activeTab) {
            case 'all': return renderTransactionsTable(filteredTransactions);
            case 'sales': return renderTransactionsTable(salesData);
            case 'purchases': return renderTransactionsTable(purchasesData);
            case 'returns': return renderTransactionsTable(returnsData);
            case 'salary': return renderTransactionsTable(salaryData);
            case 'expense': return renderTransactionsTable(expensesData);
            case 'profit_loss': return renderProfitLossTab();
            default: return renderTransactionsTable(filteredTransactions);
        }
    };

    // ── Render full page ledger view with A4 print styles ──
    if (isLedgerView) {
        // ── FIX: Recalculate totals using grand_total ──
        let totalAmount = 0;
        let totalPaid = 0;
        let totalBalance = 0;

        filteredTransactions.forEach(tx => {
            const originalAmount = parseFloat(tx.grand_total || tx.amount || 0);
            const paid = parseFloat(tx.amount_paid || tx.paid_amount || 0);
            const balance = originalAmount - paid;
            const balanceFromApi = parseFloat(tx.amount_due || tx.balance || 0);
            const finalBalance = balanceFromApi > 0 ? balanceFromApi : (balance > 0 ? balance : 0);

            totalAmount += originalAmount;
            totalPaid += paid;
            totalBalance += finalBalance;
        });

        return (
            <div className={`oztransui-ledger-print-container ${isRTL ? 'oztransui-rtl' : 'oztransui-ltr'}`}>
                <div className="oztransui-ledger-print-header">
                    <div className="oztransui-ledger-print-actions">
                        <button className="oztransui-btn-print-ledger" onClick={() => window.print()}>
                            <FontAwesomeIcon icon={faPrint} /> Print / PDF
                        </button>
                        <button className="oztransui-btn-close-ledger" onClick={() => window.close()}>
                            <FontAwesomeIcon icon={faTimes} /> Close
                        </button>
                    </div>
                </div>

                <div className="oztransui-ledger-print-content" id="ledger-print-content">
                    {/* Header */}
                    <div className="oztransui-ledger-print-header-section">
                        <div className="oztransui-ledger-print-brand">
                            {businessInfo?.bussinesslogo_url && (
                                <img src={businessInfo.bussinesslogo_url} alt="Logo" className="oztransui-ledger-print-logo" />
                            )}
                            <div className="oztransui-ledger-print-title">
                                <h1>{businessInfo?.businessname || currentShop?.shop_name || 'Complete Transactions Ledger'}</h1>
                                <p>{businessInfo?.businessaddress || ''}</p>
                                <p className="oztransui-ledger-print-contact">
                                    {businessInfo?.phonenumber && <span>📞 {businessInfo.phonenumber}</span>}
                                    {businessInfo?.email && <span>✉ {businessInfo.email}</span>}
                                </p>
                            </div>
                        </div>
                        <div className="oztransui-ledger-print-meta">
                            <div className="oztransui-ledger-print-date">
                                <strong>Generated:</strong> {formatDateFull(new Date())}
                            </div>
                            <div className="oztransui-ledger-print-filters">
                                {filters.start_date && <span className="oztransui-print-filter-tag">From: {formatDate(filters.start_date)}</span>}
                                {filters.end_date && <span className="oztransui-print-filter-tag">To: {formatDate(filters.end_date)}</span>}
                                {filters.transaction_type && <span className="oztransui-print-filter-tag">Type: {getTransactionTypeLabel(filters.transaction_type)}</span>}
                                {filters.status && <span className="oztransui-print-filter-tag">Status: {getStatusLabel(filters.status)}</span>}
                                {filters.search && <span className="oztransui-print-filter-tag">Search: {filters.search}</span>}
                            </div>
                        </div>
                    </div>

                    {/* Summary Cards */}
                    <div className="oztransui-ledger-print-summary">
                        <div className="oztransui-print-summary-card">
                            <span className="oztransui-print-summary-label">Total Amount</span>
                            <span className="oztransui-print-summary-value">Rs. {formatCurrency(totalAmount)}</span>
                        </div>
                        <div className="oztransui-print-summary-card">
                            <span className="oztransui-print-summary-label">Total Paid</span>
                            <span className="oztransui-print-summary-value" style={{ color: '#3b82f6' }}>Rs. {formatCurrency(totalPaid)}</span>
                        </div>
                        <div className="oztransui-print-summary-card">
                            <span className="oztransui-print-summary-label">Total Balance</span>
                            <span className="oztransui-print-summary-value" style={{ color: totalBalance > 0 ? '#d97706' : '#059669' }}>Rs. {formatCurrency(totalBalance)}</span>
                        </div>
                        <div className="oztransui-print-summary-card">
                            <span className="oztransui-print-summary-label">Transactions</span>
                            <span className="oztransui-print-summary-value">{filteredTransactions.length}</span>
                        </div>
                    </div>

                    {/* Transactions Table */}
                    <div className="oztransui-ledger-print-table-wrapper">
                        <table className="oztransui-ledger-print-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Date</th>
                                    <th>Type</th>
                                    <th>Reference</th>
                                    <th>Description</th>
                                    <th style={{ textAlign: 'right' }}>Grand Total</th>
                                    <th style={{ textAlign: 'right' }}>Paid</th>
                                    <th style={{ textAlign: 'right' }}>Balance</th>
                                    <th style={{ textAlign: 'center' }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTransactions.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                                            No transactions found
                                        </td>
                                    </tr>
                                ) : (
                                    filteredTransactions.map((tx, index) => {
                                        const originalAmount = parseFloat(tx.grand_total || tx.amount || 0);
                                        const paid = parseFloat(tx.amount_paid || tx.paid_amount || 0);
                                        const balance = originalAmount - paid;
                                        const balanceFromApi = parseFloat(tx.amount_due || tx.balance || 0);
                                        const finalBalance = balanceFromApi > 0 ? balanceFromApi : (balance > 0 ? balance : 0);
                                        return (
                                            <tr key={tx.transaction_id || index}>
                                                <td style={{ textAlign: 'center' }}>{index + 1}</td>
                                                <td>{formatDate(tx.transaction_date || tx.date)}</td>
                                                <td>
                                                    <span className={`oztransui-print-type-badge ${getTransactionTypeClass(tx.transaction_type)}`}>
                                                        {getTransactionTypeLabel(tx.transaction_type, tx)}
                                                    </span>
                                                </td>
                                                <td>{tx.reference_name || '-'}</td>
                                                <td>{tx.description || '-'}</td>
                                                <td style={{ textAlign: 'right', fontWeight: '600' }}>Rs. {formatCurrency(originalAmount)}</td>
                                                <td style={{ textAlign: 'right', fontWeight: '500' }}>Rs. {formatCurrency(paid)}</td>
                                                <td style={{ textAlign: 'right', fontWeight: '600', color: finalBalance > 0 ? '#d97706' : '#059669' }}>Rs. {formatCurrency(finalBalance)}</td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <span className={`oztransui-print-status-badge ${getStatusClass(tx.status || tx.payment_status)}`}>
                                                        {getStatusLabel(tx.status || tx.payment_status)}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan="5" style={{ textAlign: 'right', fontWeight: '700', fontSize: '13px' }}>TOTALS</td>
                                    <td style={{ textAlign: 'right', fontWeight: '700', fontSize: '13px' }}>Rs. {formatCurrency(totalAmount)}</td>
                                    <td style={{ textAlign: 'right', fontWeight: '700', fontSize: '13px' }}>Rs. {formatCurrency(totalPaid)}</td>
                                    <td style={{ textAlign: 'right', fontWeight: '700', fontSize: '13px', color: totalBalance > 0 ? '#d97706' : '#059669' }}>Rs. {formatCurrency(totalBalance)}</td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    {/* Footer */}
                    <div className="oztransui-ledger-print-footer">
                        <span>Generated on: {formatDateFull(new Date())}</span>
                        <span>Powered by OurZest Pvt Ltd.</span>
                    </div>
                </div>

                {/* Print Styles - A4 optimized */}
                <style dangerouslySetInnerHTML={{
                    __html: `
                    @media print {
                        body { 
                            background: #fff !important; 
                            margin: 0 !important; 
                            padding: 0 !important;
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                        }
                        .oztransui-ledger-print-header { display: none !important; }
                        .oztransui-ledger-print-container { 
                            padding: 0 !important; 
                            margin: 0 !important;
                            max-width: 100% !important;
                        }
                        .oztransui-ledger-print-content {
                            padding: 20px !important;
                            margin: 0 auto !important;
                            max-width: 100% !important;
                        }
                        .oztransui-ledger-print-table-wrapper {
                            overflow: visible !important;
                        }
                        .oztransui-ledger-print-table {
                            font-size: 9pt !important;
                            width: 100% !important;
                            border-collapse: collapse !important;
                        }
                        .oztransui-ledger-print-table th {
                            background: #10b981 !important;
                            color: #fff !important;
                            padding: 6px 8px !important;
                            font-size: 8pt !important;
                            text-transform: uppercase !important;
                            letter-spacing: 0.5px !important;
                            border: 1px solid #d1d5db !important;
                        }
                        .oztransui-ledger-print-table td {
                            padding: 5px 8px !important;
                            border: 1px solid #d1d5db !important;
                            font-size: 8.5pt !important;
                        }
                        .oztransui-ledger-print-table tbody tr:nth-child(even) {
                            background: #f9fafb !important;
                        }
                        .oztransui-ledger-print-table tfoot td {
                            background: #f3f4f6 !important;
                            font-weight: 700 !important;
                            border-top: 2px solid #1f2937 !important;
                        }
                        .oztransui-print-summary-card {
                            border: 1px solid #e5e7eb !important;
                            background: #f9fafb !important;
                            padding: 10px 16px !important;
                            text-align: center !important;
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                        }
                        .oztransui-print-type-badge {
                            padding: 2px 8px !important;
                            font-size: 7.5pt !important;
                            font-weight: 600 !important;
                            border-radius: 0 !important;
                            display: inline-block !important;
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                        }
                        .oztransui-print-status-badge {
                            padding: 2px 10px !important;
                            font-size: 7.5pt !important;
                            font-weight: 600 !important;
                            border-radius: 0 !important;
                            display: inline-block !important;
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                        }
                        .oztransui-ledger-print-summary {
                            display: grid !important;
                            grid-template-columns: repeat(4, 1fr) !important;
                            gap: 12px !important;
                            margin: 16px 0 !important;
                        }
                        .oztransui-print-filter-tag {
                            display: inline-block !important;
                            padding: 2px 8px !important;
                            background: #f3f4f6 !important;
                            border: 1px solid #e5e7eb !important;
                            font-size: 7pt !important;
                            margin: 2px !important;
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                        }
                        .oztransui-ledger-print-footer {
                            margin-top: 16px !important;
                            padding-top: 10px !important;
                            border-top: 1px solid #e5e7eb !important;
                            font-size: 8pt !important;
                            color: #94a3b8 !important;
                            display: flex !important;
                            justify-content: space-between !important;
                        }
                        .oztransui-ledger-print-header-section {
                            display: flex !important;
                            justify-content: space-between !important;
                            align-items: flex-start !important;
                            padding-bottom: 12px !important;
                            border-bottom: 3px solid #10b981 !important;
                            margin-bottom: 16px !important;
                            flex-wrap: wrap !important;
                            gap: 10px !important;
                        }
                        .oztransui-ledger-print-brand {
                            display: flex !important;
                            align-items: center !important;
                            gap: 14px !important;
                        }
                        .oztransui-ledger-print-logo {
                            max-height: 50px !important;
                            max-width: 80px !important;
                            object-fit: contain !important;
                        }
                        .oztransui-ledger-print-title h1 {
                            font-size: 16pt !important;
                            font-weight: 700 !important;
                            color: #1f2937 !important;
                            margin: 0 !important;
                        }
                        .oztransui-ledger-print-title p {
                            font-size: 8pt !important;
                            color: #6b7280 !important;
                            margin: 2px 0 !important;
                        }
                        .oztransui-ledger-print-meta {
                            text-align: right !important;
                            font-size: 8pt !important;
                            color: #6b7280 !important;
                        }
                        .oztransui-ledger-print-date {
                            margin-bottom: 4px !important;
                        }
                        /* Page break */
                        .oztransui-ledger-print-table-wrapper {
                            page-break-inside: auto !important;
                        }
                        .oztransui-ledger-print-table tbody tr {
                            page-break-inside: avoid !important;
                        }
                        .oztransui-ledger-print-table thead {
                            display: table-header-group !important;
                        }
                        .oztransui-ledger-print-table tfoot {
                            display: table-footer-group !important;
                        }
                        /* RTL Print */
                        .oztransui-rtl .oztransui-ledger-print-table th,
                        .oztransui-rtl .oztransui-ledger-print-table td {
                            text-align: right !important;
                        }
                        .oztransui-rtl .oztransui-ledger-print-meta {
                            text-align: left !important;
                        }
                    }
                    @page {
                        size: A4;
                        margin: 10mm 12mm;
                    }
                `}} />
            </div>
        );
    }

    if (!currentShop) {
        return (
            <div className={`oztransui-container ${isRTL ? 'oztransui-rtl' : 'oztransui-ltr'} ${darkMode ? 'oztransui-dark' : ''}`}>
                <div className="oztransui-no-shop">
                    <FontAwesomeIcon icon={faStore} />
                    <h3>No Shop Selected</h3>
                    <p>Select a shop first</p>
                </div>
            </div>
        );
    }

    const totalPages = pagination.totalPages;
    const activeTransactionMeta = transactionTypeMeta[transactionType] || { icon: faExchangeAlt, tone: 'primary' };
    const isSupplierPayment = transactionType === 'supplier_payment';
    const isSupplierDue = transactionType === 'supplier_due';

    return (
        <div className={`oztransui-container ${isRTL ? 'oztransui-rtl' : 'oztransui-ltr'} ${darkMode ? 'oztransui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            {/* ── TABS ── */}
            <div className="oztransui-header-actions-bar">
                <div className="oztransui-tabs">
                    {[
                        { id: 'all', icon: faExchangeAlt, label: t('allTransactions'), count: filteredTransactions.length },
                        { id: 'sales', icon: faShoppingCart, label: t('sales'), count: salesData.length },
                        { id: 'purchases', icon: faCartPlus, label: t('purchases'), count: purchasesData.length },
                        { id: 'returns', icon: faUndo, label: t('returns'), count: returnsData.length },
                        { id: 'salary', icon: faUserTie, label: t('salaries'), count: salaryData.length },
                        { id: 'expense', icon: faReceipt, label: t('expenses'), count: expensesData.length },
                        { id: 'profit_loss', icon: faBalanceScale, label: t('profitLoss'), count: null },
                    ].map(tab => (
                        <button key={tab.id} className={`oztransui-tab ${activeTab === tab.id ? 'active' : ''}`} onClick={() => handleTabChange(tab.id)}>
                            <FontAwesomeIcon icon={tab.icon} />
                            <span className="oztransui-tab-label">{tab.label}</span>
                            {tab.count !== null && <span className="oztransui-tab-count">{tab.count}</span>}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── FILTERS ── */}
            {activeTab !== 'profit_loss' && (
                <div className="oztransui-filters-section">
                    <div className="oztransui-filters-scroll-inner">
                        <div className="oztransui-filters-row">
                            <div className="oztransui-filter-group">
                                <div className="oztransui-floating-wrapper">
                                    <input type="date" id="startDate" value={filters.start_date} onChange={e => setFilters(prev => ({ ...prev, start_date: e.target.value }))} placeholder=" " />
                                    <label htmlFor="startDate"><FontAwesomeIcon icon={faCalendarAlt} />{t('startDate') || 'Start Date'}</label>
                                </div>
                            </div>
                            <div className="oztransui-filter-group">
                                <div className="oztransui-floating-wrapper">
                                    <input type="date" id="endDate" value={filters.end_date} onChange={e => setFilters(prev => ({ ...prev, end_date: e.target.value }))} placeholder=" " />
                                    <label htmlFor="endDate"><FontAwesomeIcon icon={faCalendarAlt} />{t('endDate') || 'End Date'}</label>
                                </div>
                            </div>

                            {/* ── Search Filter ── */}
                            <div className="oztransui-filter-group oztransui-search-filter">
                                <div className="oztransui-floating-wrapper">
                                    <input
                                        type="text"
                                        id="searchFilter"
                                        value={filters.search || ''}
                                        onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                        placeholder=" "
                                    />
                                    <label htmlFor="searchFilter"><FontAwesomeIcon icon={faSearch} /> {t('searchNameSupplier') || 'Search Name / Supplier'}</label>
                                </div>
                            </div>

                            <div className="oztransui-actions">
                                <CustomDropdown label={t('selectType')} icon={faTag} value={filters.transaction_type} onChange={val => setFilters(prev => ({ ...prev, transaction_type: val }))} options={filterTransactionTypeOptions} translations={translations} />
                                <button className="oztransui-btn-ledger" onClick={openLedger}><FontAwesomeIcon icon={faFileInvoice} /> <span>{t('viewLedger') || 'View Ledger'}</span></button>
                              
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── LOADING OVERLAY ── */}
            {(loading || refreshing) && activeTab !== 'profit_loss' && getCurrentTabData().length === 0 && (
                <div className="oztransui-loading-overlay">
                    <div className="oztransui-loading-spinner-large">
                        <FontAwesomeIcon icon={faSpinner} spin size="3x" />
                        <p>{t('loading')}</p>
                    </div>
                </div>
            )}

            {/* ── TAB CONTENT ── */}
            {renderTabContent()}

            {/* ── PAGINATION ── */}
            {activeTab !== 'profit_loss' && totalPages > 1 && !loading && getCurrentTabData().length > 0 && (
                <div className="ozlistui-pagination">
                    <div className="ozlistui-pagination-info">
                        <span>{t('showing')} {((pagination.currentPage - 1) * pagination.limit) + 1} {t('of')} {getCurrentTabData().length} {t('transactions')}</span>
                    </div>
                    <div className="ozlistui-pagination-controls">
                        <button className="ozlistui-pagination-btn" disabled={pagination.currentPage === 1} onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}>
                            <FontAwesomeIcon icon={faChevronLeft} /><span>{t('previous')}</span>
                        </button>
                        <div className="ozlistui-page-numbers">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                let page;
                                if (totalPages <= 5) page = i + 1;
                                else if (pagination.currentPage <= 3) page = i + 1;
                                else if (pagination.currentPage >= totalPages - 2) page = totalPages - 4 + i;
                                else page = pagination.currentPage - 2 + i;
                                return (
                                    <button key={page} onClick={() => setPagination(prev => ({ ...prev, currentPage: page }))} className={`ozlistui-page-number ${pagination.currentPage === page ? 'ozlistui-active' : ''}`}>{page}</button>
                                );
                            })}
                        </div>
                        <button className="ozlistui-pagination-btn" disabled={pagination.currentPage === totalPages} onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}>
                            <span>{t('next')}</span><FontAwesomeIcon icon={faChevronRight} />
                        </button>
                    </div>
                </div>
            )}

            {/* ── ADD TRANSACTION MODAL ── */}
            {showAddModal && (
                <div className="oztransui-modal-overlay" onClick={() => setShowAddModal(false)}>
                    <div
                        ref={modalRef}
                        className={`oztransui-modal-content oztransui-addtxn-modal ${isSupplierPayment ? 'oztransui-supplier-payment-modal' : ''}`}
                        onClick={e => e.stopPropagation()}
                        style={{ position: 'fixed', left: modalPosition.x || '50%', top: modalPosition.y || '50%', transform: modalPosition.x === 0 && modalPosition.y === 0 ? 'translate(-50%, -50%)' : 'none', width: modalSize.width, minWidth: '380px', maxWidth: '95vw', maxHeight: '88vh' }}
                    >
                        {['top', 'bottom', 'left', 'right', 'nw', 'ne', 'sw', 'se'].map(dir => (
                            <div key={dir} className={`oztransui-resize-handle-${dir}`} onMouseDown={e => startResize(e, dir === 'top' ? 'n' : dir === 'bottom' ? 's' : dir)}></div>
                        ))}
                        <div className="oztransui-modal-header oztransui-addtxn-header" onMouseDown={startDrag}>
                            <div className="oztransui-addtxn-header-left">
                                <span className={`oztransui-addtxn-header-icon oztransui-pl-tone-${activeTransactionMeta.tone}`}><FontAwesomeIcon icon={faPlus} /></span>
                                <div className="oztransui-addtxn-header-text">
                                    <h2>{t('addTransaction')}</h2>
                                    <span>{t('addNewTransaction') || 'Record a new financial transaction'}</span>
                                </div>
                            </div>
                            <div className="oztransui-addtxn-header-right">
                                <div className="oztransui-width-control-group">
                                    {Object.entries(widthModes).map(([key, mode]) => (
                                        <button key={key} className={`oztransui-width-btn ${currentWidthMode === key ? 'oztransui-active' : ''}`} onClick={() => setWidthMode(key)} title={mode.label}>
                                            <FontAwesomeIcon icon={mode.icon} />
                                        </button>
                                    ))}
                                </div>
                                <button className="oztransui-modal-close" onClick={() => setShowAddModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
                            </div>
                        </div>

                        <div className="oztransui-modal-body oztransui-addtxn-body">
                            {/* Transaction Type */}
                            <div className="oztransui-modal-section">
                                <div className="oztransui-modal-section-title"><FontAwesomeIcon icon={faLayerGroup} /><span>{t('transactionTypeSection') || 'Transaction Type'}</span></div>
                                <div className="oztransui-modal-grid oztransui-modal-grid-1">
                                    <CustomDropdown ref={firstInputRef} label={t('selectType')} icon={activeTransactionMeta.icon} value={transactionType} onChange={val => { setTransactionType(val); resetForm(); }} options={transactionTypeOptions} translations={translations} disabled={loading} />
                                </div>
                            </div>

                            {/* Reference - Supplier Payment specific UI */}
                            {isSupplierPayment ? (
                                // ── SUPPLIER PAYMENT UI ──
                                <>
                                    <div className="oztransui-modal-section">
                                        <div className="oztransui-modal-section-title"><FontAwesomeIcon icon={faTruck} /><span>{t('referenceSection') || 'Reference'}</span></div>
                                        <div className="oztransui-modal-grid oztransui-modal-grid-1">
                                            <CustomDropdown
                                                label={t('selectSupplier')}
                                                icon={faTruck}
                                                value={selectedSupplierForPayment || ''}
                                                onChange={(val) => {
                                                    handleSupplierSelectForPayment(val);
                                                    setFormData(prev => ({ ...prev, reference_id: val }));
                                                }}
                                                options={suppliers.map(s => ({ value: s.id?.toString() || s.supplierid?.toString(), label: s.supplier_name || s.name }))}
                                                translations={translations}
                                                disabled={loading}
                                            />
                                        </div>
                                    </div>

                                    {selectedSupplierForPayment && (
                                        <>
                                            {/* Unpaid Invoices Section */}
                                            <div className="oztransui-modal-section">
                                                <div className="oztransui-modal-section-title">
                                                    <FontAwesomeIcon icon={faFileInvoice} />
                                                    <span>{t('unpaidInvoices') || 'Unpaid Invoices'}</span>
                                                    <span className="oztransui-badge-count">{supplierUnpaidInvoices.length}</span>
                                                </div>
                                                <div className="oztransui-modal-grid oztransui-modal-grid-1">
                                                    {supplierUnpaidInvoices.length === 0 ? (
                                                        <div className="oztransui-no-data-small">
                                                            <FontAwesomeIcon icon={faCheckCircle} />
                                                            <span>{t('noUnpaidInvoices') || 'No unpaid invoices found for this supplier'}</span>
                                                        </div>
                                                    ) : (
                                                        <div className="oztransui-invoice-list">
                                                            {supplierUnpaidInvoices.map((inv, idx) => (
                                                                <div
                                                                    key={inv.transaction_id || idx}
                                                                    className={`oztransui-invoice-item ${selectedInvoiceForPayment?.transaction_id === inv.transaction_id ? 'oztransui-selected' : ''}`}
                                                                    onClick={() => handleInvoiceSelectForPayment(inv)}
                                                                >
                                                                    <div className="oztransui-invoice-info">
                                                                        <span className="oztransui-invoice-number">{inv.invoice_number || `INV-${inv.transaction_id}`}</span>
                                                                        <span className="oztransui-invoice-date">{formatDate(inv.transaction_date)}</span>
                                                                    </div>
                                                                    <div className="oztransui-invoice-amounts">
                                                                        <span className="oztransui-invoice-total">Rs. {formatCurrency(inv.amount || 0)}</span>
                                                                        <span className="oztransui-invoice-due">Due: Rs. {formatCurrency(inv.amount_due || inv.balance || 0)}</span>
                                                                    </div>
                                                                    <div className="oztransui-invoice-status">
                                                                        <span className={`oztransui-status-badge oztransui-status-${getStatusClass(inv.status || 'pending')}`}>
                                                                            {getStatusLabel(inv.status || 'pending')}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Payment Amount Section */}
                                            <div className="oztransui-modal-section">
                                                <div className="oztransui-modal-section-title"><FontAwesomeIcon icon={faMoneyBillWave} /><span>{t('paymentAmount') || 'Payment Amount'}</span></div>
                                                <div className="oztransui-modal-grid oztransui-modal-grid-2">
                                                    <div className="oztransui-form-group">
                                                        <span className="oztransui-currency-label">Rs.</span>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            placeholder=" "
                                                            className="oztransui-amount-input"
                                                            value={paymentAmount || formData.amount}
                                                            onChange={e => {
                                                                const val = e.target.value;
                                                                setPaymentAmount(val);
                                                                setFormData(prev => ({ ...prev, amount: val }));
                                                                handlePaymentAmountChange(val);
                                                            }}
                                                            disabled={loading}
                                                        />
                                                        <label><FontAwesomeIcon icon={faDollarSign} />{t('amount')}</label>
                                                    </div>
                                                    <div className="oztransui-form-group">
                                                        <input
                                                            type="date"
                                                            placeholder=" "
                                                            value={formData.transaction_date}
                                                            onChange={e => setFormData(prev => ({ ...prev, transaction_date: e.target.value }))}
                                                            disabled={loading}
                                                        />
                                                        <label><FontAwesomeIcon icon={faCalendarAlt} />{t('date')}</label>
                                                    </div>
                                                    <CustomDropdown
                                                        label={t('paymentMethod')}
                                                        icon={faCreditCard}
                                                        value={formData.payment_method}
                                                        onChange={val => setFormData(prev => ({ ...prev, payment_method: val }))}
                                                        options={paymentMethodOptions}
                                                        translations={translations}
                                                        disabled={loading}
                                                    />
                                                    <div className="oztransui-form-group">
                                                        <input
                                                            type="text"
                                                            placeholder=" "
                                                            value={formData.invoice_number}
                                                            onChange={e => setFormData(prev => ({ ...prev, invoice_number: e.target.value }))}
                                                            disabled={loading}
                                                        />
                                                        <label><FontAwesomeIcon icon={faFileInvoice} />{t('invoiceNumber')} <span className="oztransui-optional-tag">({t('optional') || 'optional'})</span></label>
                                                    </div>
                                                </div>
                                                {selectedInvoiceForPayment && (
                                                    <div className="oztransui-payment-preview">
                                                        <div className="oztransui-payment-preview-row">
                                                            <span>{t('grandTotal') || 'Grand Total'}:</span>
                                                            <span>Rs. {formatCurrency(selectedInvoiceForPayment.amount || 0)}</span>
                                                        </div>
                                                        <div className="oztransui-payment-preview-row">
                                                            <span>{t('paidAmount') || 'Already Paid'}:</span>
                                                            <span>Rs. {formatCurrency(selectedInvoiceForPayment.amount_paid || selectedInvoiceForPayment.paid_amount || 0)}</span>
                                                        </div>
                                                        <div className="oztransui-payment-preview-row oztransui-highlight">
                                                            <span>{t('balanceDue') || 'Balance Due'}:</span>
                                                            <span>Rs. {formatCurrency(selectedInvoiceForPayment.amount_due || selectedInvoiceForPayment.balance || 0)}</span>
                                                        </div>
                                                        <div className="oztransui-payment-preview-row oztransui-payment-type">
                                                            <span>{t('paymentType') || 'Payment Type'}:</span>
                                                            <span className={isFullPayment ? 'oztransui-full-payment' : 'oztransui-partial-payment'}>
                                                                {isFullPayment ? t('fullPayment') || 'Full Payment' : t('partialPayment') || 'Partial Payment'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Notes */}
                                            <div className="oztransui-modal-section">
                                                <div className="oztransui-modal-section-title"><FontAwesomeIcon icon={faStickyNote} /><span>{t('notesSection') || 'Notes & Reference'}</span></div>
                                                <div className="oztransui-modal-grid oztransui-modal-grid-1">
                                                    <div className="oztransui-form-group">
                                                        <textarea
                                                            rows="2"
                                                            placeholder=" "
                                                            value={formData.description}
                                                            onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                                            disabled={loading}
                                                        />
                                                        <label><FontAwesomeIcon icon={faStickyNote} />{t('description')}</label>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </>
                            ) : isSupplierDue ? (
                                // ── SUPPLIER DUE UI ──
                                <>
                                    <div className="oztransui-modal-section">
                                        <div className="oztransui-modal-section-title"><FontAwesomeIcon icon={faTruck} /><span>{t('referenceSection') || 'Reference'}</span></div>
                                        <div className="oztransui-modal-grid oztransui-modal-grid-1">
                                            <CustomDropdown
                                                label={t('selectSupplier')}
                                                icon={faTruck}
                                                value={formData.reference_id}
                                                onChange={val => setFormData(prev => ({ ...prev, reference_id: val }))}
                                                options={suppliers.map(s => ({ value: s.id?.toString() || s.supplierid?.toString(), label: s.supplier_name || s.name }))}
                                                translations={translations}
                                                disabled={loading}
                                            />
                                        </div>
                                    </div>

                                    <div className="oztransui-modal-section">
                                        <div className="oztransui-modal-section-title"><FontAwesomeIcon icon={faWallet} /><span>{t('paymentDetailsSection') || 'Payment Details'}</span></div>
                                        <div className="oztransui-modal-grid oztransui-modal-grid-2">
                                            <div className="oztransui-form-group">
                                                <span className="oztransui-currency-label">Rs.</span>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    placeholder=" "
                                                    className="oztransui-amount-input"
                                                    value={formData.amount}
                                                    onChange={e => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                                                    disabled={loading}
                                                />
                                                <label><FontAwesomeIcon icon={faDollarSign} />{t('amount')}</label>
                                            </div>
                                            <div className="oztransui-form-group">
                                                <input
                                                    type="date"
                                                    placeholder=" "
                                                    value={formData.transaction_date}
                                                    onChange={e => setFormData(prev => ({ ...prev, transaction_date: e.target.value }))}
                                                    disabled={loading}
                                                />
                                                <label><FontAwesomeIcon icon={faCalendarAlt} />{t('date')}</label>
                                            </div>
                                            <CustomDropdown
                                                label={t('paymentMethod')}
                                                icon={faCreditCard}
                                                value={formData.payment_method}
                                                onChange={val => setFormData(prev => ({ ...prev, payment_method: val }))}
                                                options={paymentMethodOptions}
                                                translations={translations}
                                                disabled={loading}
                                            />
                                            <div className="oztransui-form-group">
                                                <input
                                                    type="text"
                                                    placeholder=" "
                                                    value={formData.invoice_number}
                                                    onChange={e => setFormData(prev => ({ ...prev, invoice_number: e.target.value }))}
                                                    disabled={loading}
                                                />
                                                <label><FontAwesomeIcon icon={faFileInvoice} />{t('invoiceNumber')} <span className="oztransui-optional-tag">({t('optional') || 'optional'})</span></label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="oztransui-modal-section">
                                        <div className="oztransui-modal-section-title"><FontAwesomeIcon icon={faStickyNote} /><span>{t('notesSection') || 'Notes & Reference'}</span></div>
                                        <div className="oztransui-modal-grid oztransui-modal-grid-1">
                                            <div className="oztransui-form-group">
                                                <textarea
                                                    rows="2"
                                                    placeholder=" "
                                                    value={formData.description}
                                                    onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                                    disabled={loading}
                                                />
                                                <label><FontAwesomeIcon icon={faStickyNote} />{t('description')}</label>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : transactionType !== 'expense' ? (
                                // ── OTHER TRANSACTION TYPES (Customer Due, Customer Payment, Employee Salary) ──
                                <>
                                    <div className="oztransui-modal-section">
                                        <div className="oztransui-modal-section-title"><FontAwesomeIcon icon={faUsers} /><span>{t('referenceSection') || 'Reference'}</span></div>
                                        <div className="oztransui-modal-grid oztransui-modal-grid-1">
                                            {(transactionType === 'customer_due' || transactionType === 'customer_payment') && (
                                                <CustomDropdown
                                                    label={t('selectCustomer')}
                                                    icon={faUsers}
                                                    value={formData.reference_id}
                                                    onChange={val => setFormData(prev => ({ ...prev, reference_id: val }))}
                                                    options={customers.map(c => ({ value: c.id?.toString() || c.customerid?.toString(), label: c.customer_name || c.name }))}
                                                    translations={translations}
                                                    disabled={loading}
                                                />
                                            )}
                                            {transactionType === 'employee_salary' && (
                                                <CustomDropdown
                                                    label={t('selectEmployee')}
                                                    icon={faUserTie}
                                                    value={formData.reference_id}
                                                    onChange={val => setFormData(prev => ({ ...prev, reference_id: val }))}
                                                    options={employees.map(e => ({ value: e.id?.toString() || e.employee_id?.toString(), label: e.employee_name || e.name }))}
                                                    translations={translations}
                                                    disabled={loading}
                                                />
                                            )}
                                        </div>
                                    </div>

                                    <div className="oztransui-modal-section">
                                        <div className="oztransui-modal-section-title"><FontAwesomeIcon icon={faWallet} /><span>{t('paymentDetailsSection') || 'Payment Details'}</span></div>
                                        <div className="oztransui-modal-grid oztransui-modal-grid-2">
                                            <div className="oztransui-form-group">
                                                <span className="oztransui-currency-label">Rs.</span>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    placeholder=" "
                                                    className="oztransui-amount-input"
                                                    value={formData.amount}
                                                    onChange={e => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                                                    disabled={loading}
                                                />
                                                <label><FontAwesomeIcon icon={faDollarSign} />{t('amount')}</label>
                                            </div>
                                            <div className="oztransui-form-group">
                                                <input
                                                    type="date"
                                                    placeholder=" "
                                                    value={formData.transaction_date}
                                                    onChange={e => setFormData(prev => ({ ...prev, transaction_date: e.target.value }))}
                                                    disabled={loading}
                                                />
                                                <label><FontAwesomeIcon icon={faCalendarAlt} />{t('date')}</label>
                                            </div>
                                            <CustomDropdown
                                                label={t('paymentMethod')}
                                                icon={faCreditCard}
                                                value={formData.payment_method}
                                                onChange={val => setFormData(prev => ({ ...prev, payment_method: val }))}
                                                options={paymentMethodOptions}
                                                translations={translations}
                                                disabled={loading}
                                            />
                                            <div className="oztransui-form-group">
                                                <input
                                                    type="text"
                                                    placeholder=" "
                                                    value={formData.invoice_number}
                                                    onChange={e => setFormData(prev => ({ ...prev, invoice_number: e.target.value }))}
                                                    disabled={loading}
                                                />
                                                <label><FontAwesomeIcon icon={faFileInvoice} />{t('invoiceNumber')} <span className="oztransui-optional-tag">({t('optional') || 'optional'})</span></label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="oztransui-modal-section">
                                        <div className="oztransui-modal-section-title"><FontAwesomeIcon icon={faStickyNote} /><span>{t('notesSection') || 'Notes & Reference'}</span></div>
                                        <div className="oztransui-modal-grid oztransui-modal-grid-1">
                                            <div className="oztransui-form-group">
                                                <textarea
                                                    rows="2"
                                                    placeholder=" "
                                                    value={formData.description}
                                                    onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                                    disabled={loading}
                                                />
                                                <label><FontAwesomeIcon icon={faStickyNote} />{t('description')}</label>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                // ── EXPENSE UI ──
                                <>
                                    <div className="oztransui-modal-section">
                                        <div className="oztransui-modal-section-title"><FontAwesomeIcon icon={faReceipt} /><span>{t('referenceSection') || 'Reference'}</span></div>
                                        <div className="oztransui-modal-grid oztransui-modal-grid-2">
                                            <div className="oztransui-form-group">
                                                <input
                                                    type="text"
                                                    placeholder=" "
                                                    value={formData.expense_category}
                                                    onChange={e => setFormData(prev => ({ ...prev, expense_category: e.target.value }))}
                                                    disabled={loading}
                                                />
                                                <label>{t('expenseCategory') || 'Expense Category'}</label>
                                            </div>
                                            <div className="oztransui-form-group">
                                                <input
                                                    type="text"
                                                    placeholder=" "
                                                    value={formData.expense_name}
                                                    onChange={e => setFormData(prev => ({ ...prev, expense_name: e.target.value }))}
                                                    disabled={loading}
                                                />
                                                <label>{t('expenseName') || 'Expense Name'}</label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="oztransui-modal-section">
                                        <div className="oztransui-modal-section-title"><FontAwesomeIcon icon={faWallet} /><span>{t('paymentDetailsSection') || 'Payment Details'}</span></div>
                                        <div className="oztransui-modal-grid oztransui-modal-grid-2">
                                            <div className="oztransui-form-group">
                                                <span className="oztransui-currency-label">Rs.</span>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    placeholder=" "
                                                    className="oztransui-amount-input"
                                                    value={formData.amount}
                                                    onChange={e => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                                                    disabled={loading}
                                                />
                                                <label><FontAwesomeIcon icon={faDollarSign} />{t('amount')}</label>
                                            </div>
                                            <div className="oztransui-form-group">
                                                <input
                                                    type="date"
                                                    placeholder=" "
                                                    value={formData.transaction_date}
                                                    onChange={e => setFormData(prev => ({ ...prev, transaction_date: e.target.value }))}
                                                    disabled={loading}
                                                />
                                                <label><FontAwesomeIcon icon={faCalendarAlt} />{t('date')}</label>
                                            </div>
                                            <CustomDropdown
                                                label={t('paymentMethod')}
                                                icon={faCreditCard}
                                                value={formData.payment_method}
                                                onChange={val => setFormData(prev => ({ ...prev, payment_method: val }))}
                                                options={paymentMethodOptions}
                                                translations={translations}
                                                disabled={loading}
                                            />
                                            <div className="oztransui-form-group">
                                                <input
                                                    type="text"
                                                    placeholder=" "
                                                    value={formData.invoice_number}
                                                    onChange={e => setFormData(prev => ({ ...prev, invoice_number: e.target.value }))}
                                                    disabled={loading}
                                                />
                                                <label><FontAwesomeIcon icon={faFileInvoice} />{t('invoiceNumber')} <span className="oztransui-optional-tag">({t('optional') || 'optional'})</span></label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="oztransui-modal-section">
                                        <div className="oztransui-modal-section-title"><FontAwesomeIcon icon={faStickyNote} /><span>{t('notesSection') || 'Notes & Reference'}</span></div>
                                        <div className="oztransui-modal-grid oztransui-modal-grid-1">
                                            <div className="oztransui-form-group">
                                                <textarea
                                                    rows="2"
                                                    placeholder=" "
                                                    value={formData.description}
                                                    onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                                    disabled={loading}
                                                />
                                                <label><FontAwesomeIcon icon={faStickyNote} />{t('description')}</label>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="oztransui-modal-footer oztransui-addtxn-footer">
                            {formData.amount ? (
                                <div className="oztransui-addtxn-preview">
                                    <span className="oztransui-addtxn-preview-label">{t('amount')}</span>
                                    <span className="oztransui-addtxn-preview-value">Rs. {formatCurrency(parseFloat(formData.amount) || 0)}</span>
                                </div>
                            ) : (
                                <div></div>
                            )}
                            <div className="oztransui-addtxn-footer-actions">
                                <button className="oztransui-btn-cancel" onClick={() => setShowAddModal(false)} disabled={loading}>{t('cancel')}</button>
                                <button className="oztransui-btn-primary" onClick={handleAddTransaction} disabled={loading}>
                                    {loading ? <div className="oztransui-spinner-small"></div> : <><FontAwesomeIcon icon={faSave} /> {t('save')}</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── PROFILE MODAL ── */}
            {showProfileModal && selectedProfile && (
                <div className="oztransui-modal-overlay" onClick={() => setShowProfileModal(false)}>
                    <div className="oztransui-modal-content oztransui-profile-modal" onClick={e => e.stopPropagation()}>
                        <div className="oztransui-modal-header">
                            <h2><FontAwesomeIcon icon={faUserTie} /> {selectedProfile.customer_name || selectedProfile.supplier_name || selectedProfile.employee_name}</h2>
                            <button className="oztransui-modal-close" onClick={() => setShowProfileModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
                        </div>
                        <div className="oztransui-modal-body">
                            <div className="oztransui-profile-summary">
                                <h3>{t('profileSummary')}</h3>
                                <div className="oztransui-summary-stats">
                                    <div className="oztransui-stat"><label>{t('totalPurchases')}:</label><span>Rs. {formatCurrency(selectedProfile.total_purchases || 0)}</span></div>
                                    <div className="oztransui-stat"><label>{t('totalPaid')}:</label><span>Rs. {formatCurrency(selectedProfile.total_paid || 0)}</span></div>
                                    <div className="oztransui-stat"><label>{t('currentDue')}:</label><span className="oztransui-due-amount">Rs. {formatCurrency(selectedProfile.total_due || 0)}</span></div>
                                </div>
                            </div>
                            <div className="oztransui-profile-transactions">
                                <h3>{t('transactionHistory') || 'Transaction History'}</h3>
                                {profileTransactions.length === 0 ? (
                                    <p className="oztransui-no-data">{t('noTransactions')}</p>
                                ) : (
                                    <table className="oztransui-transactions-table oztransui-compact">
                                        <thead>
                                            <tr><th>{t('serialNo')}</th><th>{t('date')}</th><th>{t('type')}</th><th>{t('amount')}</th><th>{t('actions')}</th></tr>
                                        </thead>
                                        <tbody>
                                            {profileTransactions.map((tx, idx) => (
                                                <tr key={tx.transaction_id} className="oztransui-clickable-row" style={{ cursor: 'pointer' }} onClick={() => handleOpenReceipt({ ...tx, customer_name: selectedProfile.customer_name || selectedProfile.supplier_name || selectedProfile.employee_name })}>
                                                    <td className="ozlistui-id-cell ozlistui-centered-cell">{idx + 1}</td>
                                                    <td>{formatDate(tx.transaction_date)}</td>
                                                    <td>{getTransactionTypeLabel(tx.transaction_type)}</td>
                                                    <td className="oztransui-amount-cell">Rs. {formatCurrency(tx.amount)}</td>
                                                    <td>
                                                        <button className="ozlistui-action-btn ozlistui-print-btn" onClick={e => { e.stopPropagation(); handleOpenReceipt({ ...tx, customer_name: selectedProfile.customer_name || selectedProfile.supplier_name || selectedProfile.employee_name }); }} title={t('viewReceipt')}>
                                                            <FontAwesomeIcon icon={faReceipt} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                        <div className="oztransui-modal-footer">
                            <button className="oztransui-btn-secondary" onClick={() => setShowProfileModal(false)}>{t('close')}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CosmeticsTransactions;